import axios from "axios";
import { ClientStatus } from "../types/client";
import { Message } from "../types/chat";
import { SupportFlowDefinition, SupportFlowConfigResponse } from "../types/supportFlow";
import { chatHistoryService } from './chatHistoryService';
import { logout } from './authService';

// Dynamic API base URL determination
let API_BASE_URL = '';

// Check for environment variable first
if (process.env.REACT_APP_API_URL) {
    API_BASE_URL = process.env.REACT_APP_API_URL;
    console.log(`[API] Using REACT_APP_API_URL: ${API_BASE_URL}`);
} else if (typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    // For local development
    API_BASE_URL = 'http://localhost:8000';
    console.log(`[API] Running in local development mode with API_BASE_URL: ${API_BASE_URL}`);
} else {
    // For production/preview, use relative URLs with Vercel rewrites
    API_BASE_URL = '';
    console.log('[API] Using relative URLs (will be handled by Vercel rewrites)');
}

// Set up Axios interceptors for authentication
axios.interceptors.request.use(
    (config) => {
        // First check for impersonation token
        const impersonationActive = localStorage.getItem('impersonation_token') !== null;
        const token = localStorage.getItem('auth_token');
        
        console.log('[API] Request interceptor:', {
            url: config.url,
            hasToken: !!token,
            impersonationActive,
            token: token ? 'present' : 'missing'
        });
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            
            // If we're in impersonation mode, add a custom header
            if (impersonationActive) {
                config.headers['X-Impersonation-Active'] = 'true';
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add response interceptor to handle auth failures
// Single-flight refresh: when the access token expires, a page can fire many
// requests that all 401 at once. They must share ONE refresh call — parallel
// refreshes with the same rotating token race each other and the loser trips
// the backend's reuse detector (looks like token theft → whole family revoked
// → surprise logout).
let refreshInFlight: Promise<string | null> | null = null;
export function refreshAccessToken(): Promise<string | null> {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            try {
                const storedRefresh = localStorage.getItem('refresh_token');
                if (!storedRefresh) return null;
                const r = await axios.post(`${API_BASE_URL}/api/auth/refresh`, { refresh_token: storedRefresh });
                if (r.data?.token) {
                    localStorage.setItem('auth_token', r.data.token);
                    if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
                    return r.data.token as string;
                }
                return null;
            } catch {
                // Refresh rejected (revoked / expired / reuse-detected).
                return null;
            } finally {
                refreshInFlight = null;
            }
        })();
    }
    return refreshInFlight;
}

axios.interceptors.response.use(
    (response) => response, // Return successful responses as-is
    async (error) => {
        // P3 sessions: access tokens live 15 minutes and refresh silently.
        // On the FIRST 401 for a request, exchange the rotating refresh token
        // for a fresh pair and replay the request — the user never notices.
        // Skipped while impersonating (that session is deliberately
        // short-lived and has no refresh; renewing here would silently swap
        // back to the consultant's own identity mid-impersonation).
        const original = error.config || {};
        // Sign-in calls carry no session: their 401 means "wrong credentials",
        // and logging out would reload the page before the form shows why.
        const SIGN_IN_PATHS = ['/api/auth/login', '/api/auth/signup', '/api/forgot-password', '/api/reset-password'];
        if (SIGN_IN_PATHS.some((path) => (original.url || '').includes(path))) {
            return Promise.reject(error);
        }
        const storedRefresh = localStorage.getItem('refresh_token');
        const impersonating = localStorage.getItem('impersonation_token') !== null;
        const isRefreshCall = (original.url || '').includes('/api/auth/refresh');
        if (error.response?.status === 401 && storedRefresh && !impersonating && !isRefreshCall && !original._retried) {
            original._retried = true;
            const freshToken = await refreshAccessToken();
            if (freshToken) {
                original.headers = { ...(original.headers || {}), Authorization: `Bearer ${freshToken}` };
                return axios(original);
            }
            // Refresh failed — fall through to the logout path below.
        }

        console.log('[API] Response interceptor error:', {
            status: error.response?.status,
            url: error.config?.url,
            hasToken: !!localStorage.getItem('auth_token')
        });
        
        // Distinguish an AUTHENTICATION failure (token missing/invalid/expired →
        // log out) from an AUTHORIZATION failure (validly authenticated, but not
        // permitted → let the caller surface a toast). The backend raises 401 for
        // every token problem; a 403 means "forbidden", which is a permission
        // denial (e.g. the role-hierarchy gate) and must NOT log the user out.
        // The ONE auth-related 403 is FastAPI's HTTPBearer rejecting MISSING
        // credentials ("Not authenticated") — caught here via the no-token /
        // known-auth-detail checks.
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        const hasToken = !!localStorage.getItem('auth_token');
        // FastAPI's HTTPBearer rejects credential problems with 403, not 401:
        // "Not authenticated" (no header) and "Invalid authentication
        // credentials" (header present but not a valid Bearer scheme). Both are
        // genuine auth failures and must still force logout; every OTHER 403 is
        // a permission denial (authenticated but forbidden) and must not.
        const AUTH_403_DETAILS = ["Not authenticated", "Invalid authentication credentials"];
        const isAuthError =
            status === 401 ||
            detail === "Authorization header missing" ||
            (status === 403 && (!hasToken || AUTH_403_DETAILS.includes(detail)));

        if (isAuthError) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_role');
            // Clear client identity too — a 401 with detail like
            // "client_not_found" means the stored client_id references a
            // deleted DB row. Keeping it in localStorage would cause the
            // same FK violation on the next request.
            localStorage.removeItem('client_id');
            localStorage.removeItem('impersonated_client_id');
            localStorage.removeItem('user_name');
            console.log('[AUTH] Authentication failed, logging out user');

            // Use the centralized logout function
            logout('/login');
        }
        
        // Always reject the promise to propagate the error to the caller
        return Promise.reject(error);
    }
);

// Add TypeScript declaration for window globals used by widget
declare global {
    interface Window {
        WIDGET_API_URL?: string;
        IS_WIDGET_MODE?: boolean;
        SKIP_CITATION_API_CALLS?: boolean;
        SKIP_API_CALLS?: boolean;
        ALLOW_CALENDAR_API?: boolean;
        ALLOW_CHAT_API?: boolean;
    }
}

// For the widget, check if we're in widget mode and use the widget API URL
export const getApiBaseUrl = () => {
    if (window.WIDGET_API_URL) {
        // Check if this is for the chat stream endpoint and we're in production widget mode
        const isWidgetMode = window.IS_WIDGET_MODE === true;
        const isProd = typeof window !== 'undefined' && window.location.hostname !== 'localhost';
        
        // If we're in widget mode and in production, ensure we use the dedicated widget endpoint for chat-stream
        if (isWidgetMode && isProd) {
            console.log('[API] Using widget API URL:', window.WIDGET_API_URL);
            return window.WIDGET_API_URL;
        }
        
        return window.WIDGET_API_URL;
    }
    return `${API_BASE_URL}/api`; // Empty string for relative URL in main frontend
};

// Export a function to get the raw API base URL (without /api suffix)
export const getApiRootUrl = () => {
    return API_BASE_URL;
};

export const getDiscoveryDefaults = async () => {
    const response = await axios.get(`${getApiBaseUrl()}/crawl/discovery-defaults`);
    return response.data;
};


export const submitSupportStep = async (payload: {
    client_id: string;
    session_id: string;
    step_id: string;
    answer: any;
    conversation_id?: string;
    guest_token?: string;
    user_id?: string;
}) => {
    const response = await axios.post(`${getApiBaseUrl()}/chat/support/step`, payload);
    return response.data;
};

export const getSupportFlowConfig = async (clientId: string): Promise<SupportFlowConfigResponse> => {
    const response = await axios.get(`${getApiBaseUrl()}/support/flows/${clientId}`);
    return response.data;
};

export const saveSupportFlowConfig = async (
    clientId: string,
    payload: {
        definition: SupportFlowDefinition;
        is_enabled: boolean;
        escalation_email?: string | null;
    }
) => {
    const response = await axios.post(`${getApiBaseUrl()}/support/flows/${clientId}`, payload);
    return response.data;
};

export const getClientStatus = async (clientId: string): Promise<ClientStatus> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/client/status/${clientId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const rescrapWebsite = async (
    clientId: string,
    url: string,
    onProgress?: (data: any) => void,
    skipDeletionAndReuse: boolean = false,
    scrapingMethod: 'internal' | 'firecrawl' = 'internal',
    enablePreview: boolean = false,
    botPurpose: string = 'general',
    customPrompt?: string,
    selectedPages?: string[],
    botType?: string
): Promise<any> => {
    if (!clientId) {
        throw new Error('Client ID is required');
    }

    try {
        const response = await axios.post(`${API_BASE_URL}/api/rescrap/${clientId}`, {
            client_id: clientId,
            url,
            skip_deletion_and_reuse: skipDeletionAndReuse,
            scraping_method: scrapingMethod,
            enable_preview: enablePreview,
            bot_purpose: botPurpose,
            bot_type: botType,
            custom_prompt: customPrompt,
            selected_pages: selectedPages
        });
        
        const responseData = response.data;

        // If progress tracking is requested, setup a stream
        if (onProgress) {
            // Create stream connection to track crawl progress
            const eventSource = createCrawlStatusStream(clientId, onProgress);
            
            // Attach the close method to the response
            responseData.closeStream = () => eventSource.close();
        }

        return responseData;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.message || 'Failed to start website crawl');
        }
        throw error;
    }
};

/**
 * Get current crawl status for a client
 * @param clientId The client ID
 * @returns The current crawl status
 */
// Keep track of pending requests to prevent duplicates
const pendingCrawlStatusRequests = new Map<string, Promise<any>>();
const throttleTimers = new Map<string, number>();

export const getCrawlStatus = async (clientId: string): Promise<any> => {
    // Return the existing promise if there's already a request in progress
    if (pendingCrawlStatusRequests.has(clientId)) {
        console.log(`[CRAWL-API] Reusing existing request for client ${clientId}`);
        return pendingCrawlStatusRequests.get(clientId);
    }
    
    // Check if we should throttle (within 2 seconds of last request)
    const lastRequestTime = throttleTimers.get(clientId) || 0;
    const now = Date.now();
    if (now - lastRequestTime < 2000) {
        console.log(`[CRAWL-API] Throttling getCrawlStatus for client ${clientId}`);
        // Return last promise if available, otherwise continue with new request
        if (pendingCrawlStatusRequests.has(clientId)) {
            return pendingCrawlStatusRequests.get(clientId);
        }
    }
    
    // Record the time of this request
    throttleTimers.set(clientId, now);
    
    try {
        // Create a new request promise
        const requestPromise = axios.get(`${API_BASE_URL}/api/crawl-status/${clientId}`)
            .then(response => {
                // On success, clear from pending map after a small delay
                setTimeout(() => {
                    pendingCrawlStatusRequests.delete(clientId);
                }, 100);
                return response.data;
            })
            .catch(error => {
                // On error, clear from pending map immediately
                pendingCrawlStatusRequests.delete(clientId);
                if (axios.isAxiosError(error)) {
                    throw new Error(error.response?.data?.detail || 'Failed to get crawl status');
                }
                throw error;
            });
        
        // Store the promise
        pendingCrawlStatusRequests.set(clientId, requestPromise);
        
        return requestPromise;
    } catch (error) {
        // This should never happen as errors are caught in the promise chain
        pendingCrawlStatusRequests.delete(clientId);
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to get crawl status');
        }
        throw error;
    }
};

/**
 * Get all active crawl jobs
 * @returns List of active crawl jobs
 */
export const getActiveCrawls = async (): Promise<any> => {
    return [];
    // Future implementation:
    // try {
    //     const response = await axios.get(`${API_BASE_URL}/api/crawl-status`);
    //     return response.data;
    // } catch (error: any) {
    //     if (axios.isAxiosError(error)) {
    //         throw new Error(error.response?.data?.detail || 'Failed to get active crawls');
    //     }
    //     throw error;
    // }
};

/**
 * Creates a server-sent event connection to monitor crawl status.
 * 
 * @param clientId Client ID to monitor
 * @param onMessage Optional callback for handling messages
 * @param onError Optional callback for errors
 * @param onComplete Optional callback when streaming completes
 * @returns EventSource object
 */
export const createCrawlStatusStream = (
    clientId: string,
    onMessage?: (data: any) => void,
    onError?: (error: any) => void,
    onComplete?: () => void
): EventSource => {
    // On Vercel, use the full URL to bypass buffering issues
    const streamUrl = `${getApiBaseUrl()}/crawl-status/${clientId}/stream`;
    
    console.log(`[CRAWL-API] Creating stream to: ${streamUrl}`);
    
    // Create and return EventSource directly to maintain backward compatibility
    const eventSource = new EventSource(streamUrl);
    
    // Flag to track if we've detected "no active crawl" message
    let noActiveCrawlDetected = false;
    
    // If callbacks are provided, connect them
    if (onMessage) {
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Check for "No active crawl" error message
                if (data.error && data.error.includes("No active crawl for this client")) {
                    console.log(`[CRAWL-API] No active crawl detected for client ${clientId}, closing stream`);
                    noActiveCrawlDetected = true;
                    eventSource.close(); // Close the connection to prevent automatic reconnection
                    
                    // Still call onMessage so client can handle this status
                    onMessage(data);
                    return;
                }
                
                // Check if the crawl was stopped or aborted
                if (data.status === 'stopped' || data.status === 'aborted') {
                    console.log(`[CRAWL-API] Crawl ${data.status} for client ${clientId}`);
                    // Don't close immediately - let the server send the close event
                }
                
                // Process normal messages
                onMessage(data);
            } catch (e) {
                console.error(`[CRAWL-API] Error parsing event data:`, e);
            }
        };
        
        // Listen for "status" events (backend sends custom event type)
        eventSource.addEventListener('status', (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Same error handling as onmessage
                if (data.error && data.error.includes("No active crawl for this client")) {
                    console.log(`[CRAWL-API] No active crawl detected for client ${clientId}, closing stream`);
                    noActiveCrawlDetected = true;
                    eventSource.close();
                    onMessage(data);
                    return;
                }
                
                if (data.status === 'stopped' || data.status === 'aborted') {
                    console.log(`[CRAWL-API] Crawl ${data.status} for client ${clientId}`);
                }
                
                // Process status updates
                onMessage(data);
            } catch (e) {
                console.error(`[CRAWL-API] Error parsing status event data:`, e);
            }
        });
    }
    
    // Listen for the close event sent by the server
    eventSource.addEventListener('close', (event) => {
        console.log(`[CRAWL-API] Received close event for client ${clientId}`);
        eventSource.close();
        if (onComplete) {
            onComplete();
        }
    });
    
    if (onComplete) {
        eventSource.addEventListener('complete', () => {
            console.log(`[CRAWL-API] Stream complete for client ${clientId}`);
            onComplete();
        });
    }
    
    // Custom error handler to prevent automatic reconnection for "No active crawl"
    const originalOnerror = eventSource.onerror;
    eventSource.onerror = (error) => {
        // If we've already detected "no active crawl", don't try to reconnect
        if (noActiveCrawlDetected) {
            console.log(`[CRAWL-API] Preventing reconnection after "No active crawl" message`);
            
            // Call close again just to be sure
            eventSource.close();
            
            // Call error callback if provided
            if (onError) onError(error);
            return;
        }
        
        console.error(`[CRAWL-API] Stream error:`, error);
        
        // Call the provided error callback
        if (onError) onError(error);
        
        // Call the original error handler if it exists (keeps compatibility with original behavior)
        if (originalOnerror && !noActiveCrawlDetected) originalOnerror.call(eventSource, error);
    };
    
    return eventSource;
};

export const stopCrawl = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/crawl-status/${clientId}/stop`);
        return response.data;
    } catch (error) {
        console.error('Error stopping crawl:', error);
        throw error;
    }
};

export const abortCrawl = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/crawl-status/${clientId}/abort`);
        return response.data;
    } catch (error) {
        console.error('Error aborting crawl:', error);
        throw error;
    }
};

export const sendChatMessage = async (clientId: string, message: string, contextData = {}) => {
    try {
        // Retrieve chat history for this client
        const chatHistory = chatHistoryService.getHistory(clientId);
        
        // Only include a reasonable number of messages for context
        const recentHistory = chatHistory.slice(-10);
        
        // Format history for API request
        const formattedHistory = recentHistory.map((msg: Message) => ({
            role: msg.role,
            content: msg.content
        }));
        
        const response = await axios.post(`${getApiBaseUrl()}/chat`, {
            client_id: clientId,
            message,
            history: formattedHistory,
            context: {
                ...contextData,
                hasHistory: formattedHistory.length > 0
            }
        });

        return response.data;
    } catch (error) {
        console.error('Error calling chat API:', error);
        throw error;
    }
};

/**
 * Create a streaming chat connection using Server-Sent Events
 * @param clientId The client ID
 * @param message The message to send
 * @param onChunk Callback for each chunk received
 * @param onComplete Callback when the stream is complete
 * @param onError Callback for errors
 * @returns A function to close the stream
 */
export const createChatStream = (
    clientId: string, 
    message: string, 
    onChunk: (chunk: any) => void,
    onComplete?: (fullResponse: any) => void,
    onError?: (error: any) => void,
    extraMetadata?: Record<string, any>,
    overrideUrl?: string
): () => void => {
    // Store full response as we receive chunks
    let fullResponse = {
        content: '',
        type: 'text',
        isFormatted: false,
        metadata: {},
        citation_sources: [],
        token_data: null
    };
    
    // Flag to track whether we detected a clear history request
    let foundClearHistoryRequest = false;

    // Flag to track whether we already received a complete event
    let receivedCompleteEvent = false;

    // Get the conversation ID if stored in localStorage
    const conversationId = localStorage.getItem('conversation_id');
    
    // Create a POST request to the chat stream endpoint
    const postData: Record<string, any> = {
        client_id: clientId,
        message,
        // Include conversation_id in the message body for the updated ChatMessage model
        conversation_id: conversationId || undefined,
        // Include metadata for additional identification
        metadata: {}
    };
    
    // Merge any extra metadata (e.g., qa_run flags, image uploads)
    if (extraMetadata && typeof extraMetadata === 'object') {
        console.log('[CHAT-API] Merging extraMetadata into postData:', extraMetadata);
        postData.metadata = { ...postData.metadata, ...extraMetadata };
    }
    
    // Generate headers including guest identification
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
    };
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('auth_token');
    
    if (authToken) {
        // For authenticated users, add auth header and don't send guest token
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('[CHAT-API] Authenticated user detected, using auth token');
    } else {
        // Only add guest token for non-authenticated users
        const guestToken = localStorage.getItem('guest_token');
        if (guestToken) {
            headers['X-Guest-Token'] = guestToken;
            // Also add to metadata
            postData.metadata.guest_token = guestToken;
        } else {
            // Generate a new guest token if none exists
            const newGuestToken = 'guest_' + Math.random().toString(36).substring(2, 15);
            localStorage.setItem('guest_token', newGuestToken);
            headers['X-Guest-Token'] = newGuestToken;
            // Also add to metadata
            postData.metadata.guest_token = newGuestToken;
        }
    }
    
    // Add widget mode header if in widget mode
    if (window.IS_WIDGET_MODE) {
        headers['X-Widget-Mode'] = 'true';
        console.log('[CHAT-API] Adding widget mode header');
    }
    
    // Add browser fingerprint for more stable guest identification
    const fingerprint = generateFingerprint();
    headers['X-Fingerprint'] = fingerprint;
    
    // Use different URLs based on environment; allow override
    const apiUrl = overrideUrl || `${getApiBaseUrl()}/chat/stream`;
    
    // Create controller for aborting the fetch if needed
    const controller = new AbortController();
    const { signal } = controller;
    
    // Buffer used to hold any partial SSE frame data that may be split across
    // network packets. This prevents JSON.parse errors caused by truncated
    // frames when the server / proxy splits a frame in the middle of its
    // payload (common in production behind Nginx / ELB).
    let eventBuffer = '';

    console.log(`[CHAT-API] Starting streaming chat request to: ${apiUrl}`);
    console.log(`[CHAT-API] Using conversation_id: ${conversationId || 'none (new conversation)'}`);
    console.log(`[CHAT-API] POST data being sent:`, postData);

    fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(postData),
        signal,
        credentials: 'omit',  // Change to 'omit' for cross-origin requests without credentials
        mode: 'cors'  // Explicitly set CORS mode
    }).then(response => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Create a reader for the response body stream
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Could not get response reader');
        }
        
        // Function to read the next chunk
        const readChunk = async () => {
            try {
                const { done, value } = await reader.read();
                
                if (done) {
                    console.log(`[CHAT-API] Stream complete for client ${clientId}`);

                    // Only call onComplete if we didn't already receive a complete event
                    // (Support flows and other responses send a complete event, so we shouldn't call it twice)
                    if (!receivedCompleteEvent) {
                        console.log('[CHAT-API] No complete event received, calling onComplete with accumulated response');
                        onComplete?.(fullResponse);
                    } else {
                        console.log('[CHAT-API] Already received complete event, skipping onComplete call');
                    }
                    return;
                }
                
                // Convert the chunk to text
                const text = new TextDecoder().decode(value);

                // Append to buffer and split only on full frame delimiter
                eventBuffer += text;
                const frames = eventBuffer.split('\n\n');
                // The last element may be an incomplete frame – keep it in buffer
                eventBuffer = frames.pop() || '';

                // Process all complete frames
                for (const event of frames) {
                    // Parse the event
                    const eventLines = event.split('\n');
                    let eventName = 'message';
                    let eventData = '';
                    
                    for (const line of eventLines) {
                        if (line.startsWith('event:')) {
                            eventName = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            eventData = line.slice(5).trim();
                        }
                    }
                    
                    // Parse the data
                    if (eventData) {
                        try {
                            const data = JSON.parse(eventData);
                            
                            // Check for clear history flag before normal processing
                            const isClearHistoryRequest = 
                                data.metadata?.clear_local_storage === true || 
                                (data.metadata?.intent === 'system' && 
                                 data.content?.toLowerCase().includes('cleared your chat history'));
                            
                            if (isClearHistoryRequest) {
                                console.log('[CHAT-API] Detected clear history response:', data);
                                foundClearHistoryRequest = true;
                                
                                // Special handling for clear history requests
                                try {
                                    // Process this chunk first (but don't continue processing stream)
                                    onChunk(data);
                                    
                                    // Clear localStorage history
                                    chatHistoryService.clearHistory(clientId);
                                    console.log('[CHAT-API] Cleared chat history from localStorage');
                                    
                                    // Create a final response with clear metadata
                                    const clearResponse = {
                                        content: data.content,
                                        type: 'text',
                                        isFormatted: false,
                                        metadata: {
                                            ...data.metadata,
                                            clear_local_storage: true,
                                            intent: 'system'
                                        }
                                    };
                                    
                                    // Trigger complete callback immediately
                                    if (onComplete) {
                                        onComplete(clearResponse);
                                    }
                                    
                                    // Notify about localStorage change
                                    window.dispatchEvent(new Event('storage'));
                                    console.log('[CHAT-API] Dispatched storage event');
                                    
                                    // Stop reading from the stream - we've handled the clear request
                                    controller.abort();
                                    return;
                                } catch (clearError) {
                                    console.error('[CHAT-API] Error handling clear history:', clearError);
                                }
                            }
                            
                            // Handle metadata and conversation_id
                            if (data.metadata) {
                                // Check if we need to clear localStorage history
                                if (data.metadata.clear_local_storage) {
                                    console.log('[CHAT-API] Received clear_local_storage flag!', data.metadata);
                                    console.log('[CHAT-API] Clearing chat history from localStorage');
                                    
                                    // Don't continue processing this chunk - it will be handled above
                                    if (foundClearHistoryRequest) {
                                        return;
                                    }
                                    
                                    try {
                                        // Clear the history
                                        chatHistoryService.clearHistory(clientId);
                                        
                                        // Store new conversation id if provided
                                        if (data.metadata.conversation_id) {
                                            localStorage.setItem('conversation_id', data.metadata.conversation_id);
                                        }
                                        
                                        // Force refresh of local storage on the window object
                                        window.dispatchEvent(new Event('storage'));
                                        
                                        // Additional notification for debugging
                                        console.log(`[CHAT-API] Dispatched storage event to notify components of localStorage change`);
                                    } catch (error) {
                                        console.error('[CHAT-API] Error clearing localStorage:', error);
                                        
                                        // Try fallback direct removal as last resort
                                        try {
                                            const fallbackKey = 'chat_history';
                                            console.log('[CHAT-API] Attempting fallback direct localStorage removal');
                                            const rawData = localStorage.getItem(fallbackKey);
                                            
                                            if (rawData) {
                                                const data = JSON.parse(rawData);
                                                const filtered = data.filter((item: any) => item.clientId !== clientId);
                                                localStorage.setItem(fallbackKey, JSON.stringify(filtered));
                                                console.log('[CHAT-API] Fallback direct removal complete');
                                                
                                                // Notify components
                                                window.dispatchEvent(new Event('storage'));
                                            }
                                        } catch (fallbackError) {
                                            console.error('[CHAT-API] Fallback clearing failed:', fallbackError);
                                        }
                                    }
                                }
                                
                                // Always store conversation_id if present
                                if (data.metadata.conversation_id && !foundClearHistoryRequest) {
                                    localStorage.setItem('conversation_id', data.metadata.conversation_id);
                                }
                            }
                            
                            // If this is a clear history request that we've already detected,
                            // stop processing further chunks to avoid React DOM conflicts
                            if (foundClearHistoryRequest) {
                                console.log('[CHAT-API] Skipping further processing after clear history');
                                return;
                            }

                            // Handle different event types
                            if (eventName === 'complete') {
                                console.log(`[CHAT-API] Received complete event`, {
                                    intent: data.metadata?.intent,
                                    image_upload_needed: data.metadata?.image_upload_needed,
                                    image_step: data.metadata?.image_step,
                                    fullData: data
                                });

                                // Mark that we received a complete event
                                receivedCompleteEvent = true;

                                // If this is a clear history response, make sure we indicate that
                                if (data.metadata?.clear_local_storage ||
                                    (data.metadata?.intent === 'system' &&
                                     data.content?.toLowerCase().includes('cleared your chat history'))) {
                                    // Add clear metadata if not already present
                                    if (!data.metadata?.clear_local_storage) {
                                        data.metadata = data.metadata || {};
                                        data.metadata.clear_local_storage = true;
                                    }

                                    foundClearHistoryRequest = true;
                                }

                                // Merge complete event data with accumulated fullResponse
                                const completeResponse = {
                                    ...fullResponse,
                                    // Override with final data from complete event
                                    content: data.content || fullResponse.content,
                                    type: data.type || fullResponse.type,
                                    isFormatted: data.is_formatted !== undefined ? data.is_formatted : fullResponse.isFormatted,
                                    metadata: {
                                        ...fullResponse.metadata,
                                        ...data.metadata
                                    },
                                    // Use citation_sources from complete event (filtered and renumbered)
                                    citation_sources: data.metadata?.citation_sources || data.citation_sources,
                                    // Include token usage data if available
                                    token_data: data.metadata?.token_data || data.token_data || fullResponse.token_data
                                };

                                console.log(`[CHAT-API] Complete response citation sources:`, completeResponse.citation_sources?.length || 0);
                                console.log(`[CHAT-API] Complete response content length:`, completeResponse.content?.length || 0);
                                console.log(`[CHAT-API] About to call onChunk and onComplete callbacks`, {
                                    foundClearHistoryRequest,
                                    hasOnComplete: !!onComplete
                                });

                                // Log token usage if available
                                if (completeResponse.token_data) {
                                    console.log(`[CHAT-API] Token usage:`, completeResponse.token_data);
                                }

                                // Call the chunk callback first (for support flow image upload detection, etc.)
                                if (!foundClearHistoryRequest) {
                                    console.log('[CHAT-API] Calling onChunk with complete data');
                                    onChunk(data);
                                }

                                // Call the complete callback with merged data
                                if (onComplete && !foundClearHistoryRequest) {
                                    console.log('[CHAT-API] Calling onComplete callback');
                                    onComplete(completeResponse);
                                } else {
                                    console.log('[CHAT-API] NOT calling onComplete:', { hasOnComplete: !!onComplete, foundClearHistoryRequest });
                                }
                            } else if (eventName === 'error') {
                                console.error(`[CHAT-API] Error event:`, data);
                                onError?.(data.error || 'Unknown error');
                            } else {
                                // Regular message - only process if not a clear history request
                                if (!foundClearHistoryRequest) {
                                    console.log(`[CHAT-API] Received chunk:`, data.type);
                                    
                                    // Process chunk normally
                                    if (data.type === 'text_chunk') {
                                        // Update the full response content
                                        fullResponse.content += data.content;
                                        
                                        // First chunk might have metadata
                                        if (data.metadata) {
                                            fullResponse.metadata = { ...fullResponse.metadata, ...data.metadata };
                                            
                                            // Check if this is a formatted response
                                            if (data.metadata.use_formatted_response) {
                                                fullResponse.isFormatted = true;
                                            }
                                            
                                            // Skip citation sources from streaming chunks - use only from complete event
                                            
                                            // Capture token usage data from chunks
                                            if (data.metadata.token_data) {
                                                fullResponse.token_data = data.metadata.token_data;
                                                console.log(`[CHAT-API] Received token usage data in text_chunk:`, data.metadata.token_data);
                                            }
                                        }
                                    } else if (data.type === 'text') {
                                        // Final content possibly with formatting
                                        fullResponse.content = data.content;
                                        fullResponse.isFormatted = data.is_formatted;
                                        
                                        // Store metadata from complete event if present
                                        if (data.metadata) {
                                            fullResponse.metadata = { ...fullResponse.metadata, ...data.metadata };
                                            
                                            // Include citation sources from final text event
                                            if (data.metadata.citation_sources && Array.isArray(data.metadata.citation_sources)) {
                                                fullResponse.citation_sources = data.metadata.citation_sources;
                                            }
                                        }
                                        
                                        // Also check for citation_sources at top level
                                        if (data.citation_sources && Array.isArray(data.citation_sources)) {
                                            fullResponse.citation_sources = data.citation_sources;
                                        }
                                        
                                        // Also check for token_data at top level
                                        if (data.token_data) {
                                            fullResponse.token_data = data.token_data;
                                            console.log(`[CHAT-API] Received token usage data in text event:`, data.token_data);
                                        }
                                    } else if (data.type === 'booking') {
                                        // Calendar booking response
                                        fullResponse = {
                                            ...data.content,
                                            type: 'booking'
                                        };
                                    } else if (data.type === 'error') {
                                        fullResponse.type = 'error';
                                        fullResponse.content = data.content;
                                        onError?.(data.error || 'Unknown error');
                                    } else if (data.type === 'clear_history') {
                                        // Direct clear history response
                                        foundClearHistoryRequest = true;
                                        data.metadata = data.metadata || {};
                                        data.metadata.clear_local_storage = true;
                                        
                                        // Process this specifically
                                        onChunk(data);
                                        
                                        // And trigger completion
                                        if (onComplete) {
                                            onComplete({
                                                content: data.message || 'Chat history cleared',
                                                type: 'text',
                                                isFormatted: false,
                                                metadata: {
                                                    clear_local_storage: true,
                                                    intent: 'system'
                                                }
                                            });
                                        }
                                        
                                        // Don't process more chunks
                                        return;
                                    }
                                    
                                    // Call the chunk callback unless it's a clear history request we've already processed
                                    onChunk(data);
                                }
                            }
                        } catch (e) {
                            console.error(`[CHAT-API] Error parsing event data:`, e, eventData);
                        }
                    }
                }
                
                // If this was a clear history request, don't continue reading chunks
                if (foundClearHistoryRequest) {
                    console.log('[CHAT-API] Stopping stream after processing clear history');
                    return;
                }
                
                // Continue reading chunks if not a clear history request
                readChunk();
            } catch (error: any) {
                if (error.name === 'AbortError') {
                    console.log(`[CHAT-API] Stream aborted for client ${clientId}`);
                } else {
                    console.error(`[CHAT-API] Error reading stream:`, error);
                    // Ensure error is in proper format before passing to onError
                    const errorMessage = error?.message || 'Unknown stream error';
                    if (onError) {
                        try {
                            onError(errorMessage);
                        } catch (callbackError) {
                            console.error('[CHAT-API] Error in onError callback:', callbackError);
                        }
                    }
                }
            }
        };
        
        // Start reading chunks
        readChunk();
    }).catch(error => {
        console.error(`[CHAT-API] Fetch error:`, error);
        // Ensure error is in proper format before passing to onError
        const errorMessage = error?.message || 'Unknown fetch error';
        if (onError) {
            try {
                onError(errorMessage);
            } catch (callbackError) {
                console.error('[CHAT-API] Error in onError callback:', callbackError);
            }
        }
    });
    
    // Return a function to abort the stream
    return () => {
        console.log(`[CHAT-API] Aborting stream for client ${clientId}`);
        controller.abort();
    };
};

// Helper function to generate a simple browser fingerprint
function generateFingerprint(): string {
    const components = [
        navigator.userAgent,
        navigator.language,
        window.screen.height,
        window.screen.width,
        window.screen.colorDepth,
        new Date().getTimezoneOffset()
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < components.length; i++) {
        const char = components.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    
    return hash.toString();
}

export const login = async (email: string, password: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
            email,
            password
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('Login error response:', error.response?.data);
            console.error('Login error status:', error.response?.status);
        }
        throw error;
    }
};

export const signup = async (name: string, email: string, password: string, referral?: string | null) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/signup`, {
            name,
            email,
            password,
            // A Solution Partner's client link. The new company is assigned to
            // that partner instead of the platform default.
            ...(referral ? { referral } : {}),
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('Signup error response:', error.response?.data);
            console.error('Signup error status:', error.response?.status);
        }
        throw error;
    }
};

/* The sign-up deck: the same signup, carrying what the guest answered,
   confirmed and skipped, and the website Orbit read while they flipped. */
export const signupWithDeck = async (payload: {
    name: string; email: string; password: string; phone?: string; referral?: string | null;
    deck: { answers: Record<string, unknown>; peeked: { key: string; value: unknown; confidence: number }[]; confirmed: string[]; skipped: string[]; website: string };
}) => {
    const response = await axios.post(`${API_BASE_URL}/api/auth/signup`, {
        name: payload.name,
        email: payload.email,
        password: payload.password,
        ...(payload.phone ? { phone: payload.phone } : {}),
        ...(payload.referral ? { referral: payload.referral } : {}),
        deck: payload.deck,
    });
    return response.data;
};

/* Add client: is this owner email usable? Well formed, not already an account,
   and its domain not already a client of the platform. */
export const checkClientEmail = async (email: string) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication token not found');
    const consultantId = JSON.parse(atob(token.split('.')[1])).consultant_id;
    const response = await axios.get(`${API_BASE_URL}/api/consultant/${consultantId}/client-check`, { params: { email } });
    return response.data as {
        email: string; valid: boolean; email_taken: boolean;
        conflict: { name: string; id: string; domain: string; signal: string; reason: string; remedy: string } | null;
    };
};

export const publicStarterDeck = async (referral?: string | null) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/starter-deck`, { params: referral ? { ref: referral } : {} });
    return response.data as {
        cards: { key: string; section: string; label: string; kind: string; options: { value: string; label: string }[]; why: string; placeholder: string; rank: number }[];
        limit: number;
        partner: { id: string; name: string } | null;
        provider?: { id: string; name: string } | null;
        referral_error: string;
    };
};

export type PeekFact = { key: string; label: string; value: unknown; value_display: string; confidence: number };

export const peekDocument = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axios.post(`${API_BASE_URL}/api/public/orbit/read-document`, form);
    return response.data as { filename: string; read: boolean; facts: PeekFact[]; facts_total: number };
};

export const publicToolSearch = async (q: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/orbit/tools`, { params: { q } });
    return response.data as { tools: { slug: string; label: string; logo: string; source: string }[] };
};

export const peekSite = async (url: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/public/orbit/peek`, { url });
    return response.data as SiteRead;
};

export type ReadStep = { key: string; text: string; state: 'doing' | 'done' | 'failed' };

/* One pass of the website read, as the server reports it starting and landing. */
export type ReadPass = {
    key: string;
    title: string;
    why: string;
    state: 'doing' | 'done' | 'empty' | 'failed';
    pages: number;
    paths: string[];
    found: number;
    line: string;
    pages_read: number;
};

export type SiteRead = {
    url: string;
    read: boolean;
    facts: PeekFact[];
    facts_total: number;
    /** Fields the read wrote to the dossier. Absent on a read that keeps nothing. */
    written?: number;
    pages_read?: number;
    pages_found?: number;
    passes?: { key: string; title: string; why: string; state: string; pages: number; paths?: string[]; found: number; line: string }[];
    articles?: { url: string; host: string; title: string; snippet: string }[];
};

/* The narrated reads. The server sends one line per real step as it takes it,
   then the same payload the plain endpoint returns. */
const readStream = async <T>(path: string, init: RequestInit, onStep: (step: ReadStep) => void,
                             onPass?: (pass: ReadPass) => void): Promise<T> => {
    const response = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', ...init });
    if (!response.ok || !response.body) throw new Error(`The read did not start (${response.status}).`);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: T | null = null;
    const take = (chunk: string) => {
        const line = chunk.trim();
        if (!line.startsWith('data:')) return;
        let payload: { type?: string; data?: T } & ReadStep;
        try {
            payload = JSON.parse(line.slice(5).trim());
        } catch {
            return;
        }
        if (payload.type === 'step') {
            onStep({ key: payload.key, text: payload.text, state: payload.state });
            return;
        }
        if (payload.type === 'pass') {
            if (onPass) onPass(payload as unknown as ReadPass);
            return;
        }
        if (payload.type === 'result' && payload.data) result = payload.data;
    };
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        parts.forEach(take);
    }
    take(buffer);
    if (!result) throw new Error('The read stopped before it finished.');
    return result;
};

export const peekSiteStream = (url: string, onStep: (step: ReadStep) => void, onPass?: (pass: ReadPass) => void) =>
    readStream<SiteRead>('/api/public/orbit/peek/stream', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
    }, onStep, onPass);

/* The same narrated read, for a company that already exists: what it finds is
   written to that company's dossier, so the cards it answers leave the deck. */
export const companySiteReadStream = (url: string, companyId: string | null,
                                      onStep: (step: ReadStep) => void, onPass?: (pass: ReadPass) => void) => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    let query = '';
    if (companyId) query = `?company_id=${encodeURIComponent(companyId)}`;
    return readStream<SiteRead>(`/api/aai/orbit-room/site-read/stream${query}`, {
        headers,
        body: JSON.stringify({ url }),
    }, onStep, onPass);
};

export const peekDocumentStream = (file: File, onStep: (step: ReadStep) => void) => {
    const form = new FormData();
    form.append('file', file);
    return readStream<{ filename: string; read: boolean; facts: PeekFact[]; facts_total: number }>(
        '/api/public/orbit/read-document/stream', { body: form }, onStep,
    );
};

export const logoutUser = (redirectPath = '/login') => {
    try {
        // Use the already imported logout function
        logout(redirectPath);
    } catch (error) {
        console.error('Logout error:', error);

        // Fallback logout mechanism if the import fails - clear ALL localStorage
        localStorage.clear();

        if (typeof window !== 'undefined') {
            window.location.href = redirectPath;
        }
    }
};

export const getEmbeddingStats = async (clientId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/embeddings/stats/${clientId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch embedding stats');
    }
};

export const getDashboardStats = async (clientId: string | null) => {
    try {
        const url = clientId 
            ? `${API_BASE_URL}/api/dashboard/stats/${clientId}`
            : `${API_BASE_URL}/api/dashboard/stats`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch dashboard stats');
    }
};

export const getClientConnections = async (clientId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/dashboard/connections/${clientId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch client connections');
    }
};

export const getTokenUsage = async (clientId: string, days: number = 30) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/dashboard/token-usage/${clientId}?days=${days}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch token usage');
    }
};

export const getDailyTokenUsage = async (clientId: string, days: number = 7) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/dashboard/token-usage/${clientId}/daily?days=${days}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch daily token usage');
    }
};

export const getDocuments = async (clientId: string) => {
    try {
        // Verify we're using the correct client ID
        const storedClientId = localStorage.getItem('client_id');
        if (clientId !== storedClientId) {
            console.warn('Client ID mismatch', { provided: clientId, stored: storedClientId });
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/documents/client/${clientId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch documents');
    }
};

export const getKnowledgeBase = async (clientId: string) => {
    try {
        console.log(`Fetching knowledge base for client: ${clientId}`);
        const response = await axios.get(`${API_BASE_URL}/api/scraped-content/${clientId}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
            }
        });
        console.log('Raw API response:', response);
        console.log('Response data:', response.data);
        
        // Return the response data directly since it's already in the correct format
        return response.data;
    } catch (error: any) {
        console.error('Knowledge base API error:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }
        throw error;
    }
};

export const getKnowledgeBasePageDetail = async (
    clientId: string,
    url: string
) => {
    try {
        const params = new URLSearchParams({ url });
        const response = await axios.get(
            `${API_BASE_URL}/api/scraped-content/${clientId}/page?${params.toString()}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error: any) {
        console.error('Knowledge base page detail API error:', error);
        throw error;
    }
};

export const getKnowledgeBasePages = async (
    clientId: string, 
    page: number = 1, 
    limit: number = 50,
    search?: string
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        
        if (search) {
            params.append('search', search);
        }
        
        const response = await axios.get(
            `${API_BASE_URL}/api/scraped-content/${clientId}/pages-metadata?${params}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        
        return response.data;
    } catch (error: any) {
        console.error('Knowledge base pages API error:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }
        throw error;
    }
};

export const resetData = async () => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/reset-data`);
        return response.data;
    } catch (error) {
        console.error('Error resetting data:', error);
        throw error;
    }
};

export const uploadDocuments = async (formData: FormData): Promise<void> => {
    try {
        await axios.post(`${API_BASE_URL}/api/documents/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to upload documents');
        }
        throw error;
    }
};

export const getClientProfile = async (clientId: string) => {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/client/${clientId}/profile`);
    return response.data;
};

export const getMyBusiness = async (clientId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/client/${clientId}/my-business`);
    return response.data;
};

export const updateClientProfile = async (clientId: string, profileData: any) => {
    const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/profile`, profileData);
    return response.data;
};

export const uploadProfileImage = async (clientId: string, formData: FormData) => {
    const response = await axios.post(`${API_BASE_URL}/api/client/${clientId}/profile-image`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

// Current-user ("me") profile — the logged-in user's OWN record, resolved from
// the auth token (NOT the shared company/client record). Used by Settings > General.
export const getMyProfile = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/users/me/profile`);
    return response.data;
};

export const updateMyProfile = async (profileData: any) => {
    const response = await axios.put(`${API_BASE_URL}/api/users/me/profile`, profileData);
    return response.data;
};

export const updateMyPassword = async (passwordData: any) => {
    const response = await axios.put(`${API_BASE_URL}/api/users/me/password`, passwordData);
    return response.data;
};

export const uploadMyProfileImage = async (formData: FormData) => {
    const response = await axios.post(`${API_BASE_URL}/api/users/me/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

export const getClientPrompts = async (clientId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/client/${clientId}/prompts`);
    return response.data;
};

export const updateClientPrompts = async (clientId: string, prompts: any[]) => {
    const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/prompts`, prompts);
    return response.data;
};

export const resetClientPrompts = async (clientId: string) => {
    try {
        // First get the client profile to get the client name
        const clientProfile = await getClientProfile(clientId);
        const clientName = clientProfile.name || '';
        
        // Call the reset endpoint with client name
        const response = await axios.post(`${API_BASE_URL}/api/client/${clientId}/prompts/reset`, {
            client_name: clientName
        });
        
        return response.data;
    } catch (error) {
        console.error('Error resetting prompts:', error);
        throw error;
    }
};

export const updateClientPersonality = async (clientId: string, personality: string) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/personality`, {
            personality: personality
        });
        return response.data;
    } catch (error) {
        console.error('Error updating personality:', error);
        throw error;
    }
};

export const getClientSettings = async (clientId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/client/${clientId}/settings`);
        return response.data;
    } catch (error) {
        console.error('Error getting client settings:', error);
        throw error;
    }
};

export const updateClientSettings = async (clientId: string, settings: any) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/settings`, settings);
        return response.data;
    } catch (error) {
        console.error('Error updating client settings:', error);
        throw error;
    }
};

export const requestPasswordReset = async (email: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/forgot-password`, { email });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const resetPassword = async (token: string, new_password: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/reset-password`, {
            token,
            new_password
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateClientPassword = async (clientId: string, passwordData: any) => {
    const response = await axios.put(`${API_BASE_URL}/api/${clientId}/password`, passwordData);
    return response.data;
};

export const getEmbeddings = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/embeddings`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
    });
    return response.data;
};

// Add these calendar-related API functions
export const connectCalendar = async (data: { username: string; apiKey: string; clientId: string }) => {
    try {
        console.log(`Connecting calendar for client ${data.clientId} with username ${data.username}`);
        const response = await axios.post(`${API_BASE_URL}/api/calendar/connect`, {
            username: data.username,
            api_key: data.apiKey,
            client_id: data.clientId
        });
        console.log('Calendar connection response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Calendar connection error:', error);
        if (axios.isAxiosError(error)) {
            // Handle both 400 and 500 errors
            const errorMessage = error.response?.data?.detail || 'Failed to connect calendar';
            throw new Error(errorMessage);
        }
        throw error;
    }
};

export const getCalendarStatus = async (clientId: string) => {
    try {
        console.log(`Getting calendar status for client ${clientId}`);
        const response = await axios.get(`${API_BASE_URL}/api/calendar/status?client_id=${clientId}`);
        
        // Enhanced logging for debugging
        if (response.data?.event_types?.length > 0) {
            console.log(`Retrieved ${response.data.event_types.length} event types`);
            console.log('First event type:', response.data.event_types[0]);
            
            // Log schedule information for debugging
            const firstEvent = response.data.event_types[0];
            if (firstEvent?.schedule?.days?.length > 0) {
                console.log('First day in schedule:', firstEvent.schedule.days[0]);
                
                // Log available time slots 
                if (firstEvent.schedule.days[0].available_hours) {
                    console.log('Available hours:', firstEvent.schedule.days[0].available_hours);
                }
                
                if (firstEvent.schedule.days[0].duration_slots) {
                    console.log('Duration-specific slots:', firstEvent.schedule.days[0].duration_slots);
                }
            }
        } else {
            console.warn('No event types found in calendar status response');
        }
        
        // Ensure the response has the expected structure
        const calendarStatus = {
            ...response.data,
            connected: response.data.connected === true,
            event_types: response.data.event_types || [],
            timezone: response.data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        // Process event types to ensure they have the correct structure for CalendarView
        if (calendarStatus.event_types && Array.isArray(calendarStatus.event_types)) {
            calendarStatus.event_types = calendarStatus.event_types.map((event: any) => {
                // Ensure each event has the required fields
                const processedEvent = {
                    ...event,
                    id: event.id || event.schedule_id || `event-${Math.random().toString(36).substring(2, 9)}`,
                    title: event.title || event.name || 'Appointment',
                    // Make sure schedule is properly structured
                    schedule: event.schedule || {
                        days: event.days || []
                    }
                };
                
                // Ensure schedule.days has the correct structure
                if (processedEvent.schedule && processedEvent.schedule.days) {
                    processedEvent.schedule.days = processedEvent.schedule.days.map((day: any) => {
                        // Don't modify the original time slots data
                        const processedDay = { ...day };
                        
                        // If we have no time slots but have duration-specific slots,
                        // extract available hours from them
                        if ((!processedDay.available_hours || processedDay.available_hours.length === 0) && 
                            processedDay.duration_slots && Object.keys(processedDay.duration_slots).length > 0) {
                            
                            // Get the first available duration slots
                            const firstDuration = Object.keys(processedDay.duration_slots)[0];
                            
                            // IMPORTANT FIX: Don't modify the original slots - use them exactly as provided
                            // This ensures we don't show slots beyond what Cal.com API provides
                            processedDay.available_hours = [...processedDay.duration_slots[firstDuration]];
                            
                            console.log(`[API DEBUG] Generated available_hours from duration_slots for day ${processedDay.date || processedDay.day}:`, 
                                        processedDay.available_hours);
                            console.log(`[API DEBUG] Original duration_slots keys: ${Object.keys(processedDay.duration_slots).join(', ')}`);
                            console.log(`[API DEBUG] Duration slots data: `, processedDay.duration_slots);
                            
                            // Ensure the original data remains available
                            processedDay._original_duration_slots = {...processedDay.duration_slots};
                        }
                        
                        // Ensure available_hours_utc property is set
                        if (processedDay.available_hours_utc === undefined) {
                            // Check if this is a Cal.com response by looking for typical Cal.com properties
                            const isCalComFormat = processedDay.weekday !== undefined || 
                                                  processedDay.duration_slots !== undefined;
                            
                            // If it appears to be Cal.com data, default to true (Cal.com uses UTC)
                            processedDay.available_hours_utc = isCalComFormat ? true : false;
                            console.log(`[API DEBUG] Set available_hours_utc to ${processedDay.available_hours_utc} for day ${processedDay.date || processedDay.day}`);
                            
                            // Add timezone debugging info
                            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                            const userOffset = new Date().getTimezoneOffset();
                            console.log(`[API DEBUG] User timezone: ${userTimezone}, UTC offset: ${-userOffset/60} hours`);
                        }
                        
                        return processedDay;
                    });
                }
                
                return processedEvent;
            });
            
            console.log('Processed event types for CalendarView:', calendarStatus.event_types);
        }
        
        return calendarStatus;
    } catch (error) {
        console.error('Error getting calendar status:', error);
        // Don't swallow the error - log and return a default response
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            console.log('Calendar not found for client - returning disconnected state');
            return { connected: false, event_types: [], timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
        }
        
        // For other errors, still throw so the UI can handle them properly
        throw error;
    }
};

export const bookMeeting = async (bookingData: {
  event_type_id: string;
  user_name: string;
  user_email: string;
  start_time: string;
  duration: number;
  client_id: string;
  notes?: string;
  timezone?: string;
}) => {
  try {
    console.log('[API] ===== BOOKING MEETING WITH CAL.COM V1 API =====');
    console.log(`[API] Time to book: ${bookingData.start_time}`);
    console.log(`[API] Event Type ID: ${bookingData.event_type_id}`);
    console.log(`[API] Client timezone: ${bookingData.timezone || 'UTC'}`);
    
    // Get client ID
    const clientId = bookingData.client_id || localStorage.getItem('client_id') || '';
    console.log(`[API] Using client ID: ${clientId}`);
    
    // API URL
    const apiUrl = `${API_BASE_URL}/api/book_meeting`;
    console.log(`[API] Booking URL: ${apiUrl}`);
    
    // Calculate end time if we have a duration
    let endTime = null;
    if (bookingData.duration) {
      try {
        // Parse the start time
        const startDate = new Date(bookingData.start_time);
        // Add the duration in minutes
        const endDate = new Date(startDate.getTime() + bookingData.duration * 60000);
        // Format the end time in the same format as the start time
        // Use the manual formatting to ensure consistent format
        const year = endDate.getUTCFullYear();
        const month = String(endDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(endDate.getUTCDate()).padStart(2, '0');
        const hour = String(endDate.getUTCHours()).padStart(2, '0');
        const minute = String(endDate.getUTCMinutes()).padStart(2, '0');
        endTime = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
        console.log(`[API] Calculated end time: ${endTime}`);
      } catch (e) {
        console.error('[API] Error calculating end time:', e);
      }
    }
    
    // Use the original start time without modifications
    // Previous code was: const formattedStartTime = bookingData.start_time.replace('Z', '.000Z');
    // which caused double milliseconds like '.000.000Z'
    const formattedStartTime = bookingData.start_time;
    console.log(`[API] Using original start time without modification: ${formattedStartTime}`);
    
    // Format the request payload according to Cal.com v1 API
    const requestPayload = {
      // Cal.com v1 API uses a different payload structure
      event_type_id: parseInt(bookingData.event_type_id),
      start_time: formattedStartTime,
      end_time: endTime,
      user_name: bookingData.user_name,
      user_email: bookingData.user_email,
      timezone: bookingData.timezone || 'UTC',
      notes: bookingData.notes || '',
      client_id: clientId,
      // These will be properly formatted in the backend:
      // - The backend will nest name/email into responses
      // - The backend will add location, language, etc.
    };
    
    // Log the complete request
    console.log('[API] Booking request payload:', JSON.stringify(requestPayload, null, 2));
    
    // Make the API call
    const response = await axios.post(apiUrl, requestPayload);
    console.log('[API] Booking response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('[API] Booking error:', error);
    
    // Parse the error to extract the detailed message from Cal.com API
    let errorMessage = 'An error occurred while booking the meeting. Please try again.';
    
    if (error.response?.data?.error) {
      // Direct error format from our API
      errorMessage = error.response.data.error;
    } else if (error.response?.data) {
      try {
        // Try to extract Cal.com specific error messages
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        }
      } catch (parseError) {
        console.error('[API] Error parsing error response:', parseError);
      }
    }
    
    // Throw an enhanced error with the extracted message
    const enhancedError = new Error(errorMessage);
    (enhancedError as any).originalError = error;
    throw enhancedError;
  }
};

// Fetch all bookings from Cal.com
export const getCalendarBookings = async (clientId: string): Promise<any> => {
  try {
    const apiUrl = `${API_BASE_URL}/api/calendar/bookings/${clientId}`;
    console.log('Fetching all calendar bookings from:', apiUrl);
    
    const response = await axios.get(apiUrl);
    console.log('Calendar bookings response:', response.data);
    
    // Return only the booked_times_by_day structure
    if (response.data && response.data.data) {
      return {
        booked_times_by_day: response.data.data.booked_times_by_day || {}
      };
    }
    
    return {
      booked_times_by_day: {}
    };
  } catch (error) {
    console.error('Error fetching calendar bookings:', error);
    // Return empty structure on error instead of throwing
    return {
      booked_times_by_day: {}
    };
  }
};

/**
 * Fetch chat history for a client
 * @param clientId The client ID
 * @param conversationId Optional conversation ID to filter by
 * @param limit Maximum number of messages to return (default 10)
 * @returns Promise that resolves to the chat history
 */
export const getChatHistory = async (
    clientId: string, 
    conversationId?: string,
    limit: number = 10
): Promise<any> => {
    try {
        let url = `${getApiBaseUrl()}/chat/history/${clientId}?limit=${limit}`;
        
        if (conversationId) {
            url += `&conversation_id=${conversationId}`;
        }
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'omit',
            mode: 'cors'
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('[CHAT-API] Error fetching chat history:', error);
        throw error;
    }
};

/**
 * Fetch chat history with citations for a client
 * @param clientId The client ID
 * @param page Current page number (1-indexed)
 * @param limit Maximum number of history items per page
 * @param searchQuery Optional search term to filter chat history
 * @returns Promise that resolves to the chat history with citations
 */
export const getChatHistoryWithCitations = async (
    clientId: string,
    options: {
        page?: number;
        limit?: number;
        searchQuery?: string;
    } = {}
): Promise<any> => {
    try {
        const { 
            page = 1, 
            limit = 10, 
            searchQuery = undefined 
        } = options;
        
        // Validate page and limit to prevent NaN errors
        const validPage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
        const validLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 10;
        
        // Calculate offset based on validated page and limit
        const offset = (validPage - 1) * validLimit;
        
        // Create search parameters with validated values
        const searchParams = new URLSearchParams({
            offset: offset.toString(),
            limit: validLimit.toString(),
            with_citations: 'true'
        });
        
        // Add search query if provided and not empty
        if (searchQuery && searchQuery.trim()) {
            searchParams.append('search', searchQuery.trim());
        }
        
        // Use axios - interceptors will automatically add authorization headers
        const response = await axios.get(
            `${API_BASE_URL}/api/client/${encodeURIComponent(clientId)}/chat-history?${searchParams.toString()}`
        );
        
        console.log('[CHAT-API] Chat history with citations retrieved successfully');
        return response.data;
    } catch (error: unknown) {
        console.error('[CHAT-API] Error fetching chat history with citations:', error);
        
        // Return structured error response instead of throwing
        return {
            success: false,
            message: error instanceof Error 
                ? error.message 
                : (error as any)?.response?.data?.message || 'Failed to fetch chat history',
            error: error
        };
    }
};

export const getCitationAnalytics = async (clientId: string) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/citations/analytics/${clientId}`);
    
    if (response.status !== 200) {
      console.error(`[CITATION-API] API returned error: ${response.status}`);
      return {
        success: false,
        message: `API returned status ${response.status}`,
        data: {
          totalCitations: 0,
          totalQueries: 0,
          topSources: [],
          citationsByDay: [],
          recentCitations: [],
          averageCitationsPerQuery: 0,
          citationRatio: 0
        }
      };

    }
    
    const data = response.data;
    return data;
  } catch (error) {
    console.error('[CITATION-API] Error fetching citation analytics:', error);
    throw error;
  }
};

export const getCitationSettings = async (clientId: string): Promise<any> => {
  try {
    console.log('[API] Fetching citation settings for client:', clientId);
    
    // Make the API call
    const response = await axios.get(`${API_BASE_URL}/api/citations/status/${clientId}`);
    console.log('[API] Raw citation settings response:', response.data);
    
    // Ensure we return a consistent format
    return {
      success: true,
      citations_enabled: response.data.citations_enabled === true
    };
  } catch (error) {
    console.error('[API] Error fetching citation settings:', error);
    return {
      success: false,
      citations_enabled: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Delete a specific chat conversation
 * @param clientId - The client ID
 * @param conversationId - The conversation ID to delete
 * @returns Promise that resolves to the deletion result
 */
export const deleteChatConversation = async (clientId: string, conversationId: string): Promise<any> => {
  try {
    console.log('[CHAT-API] Deleting conversation:', { clientId, conversationId });
    
    await axios.delete(`${API_BASE_URL}/api/chat/conversation/${clientId}/${conversationId}`);
    
    console.log('[CHAT-API] Conversation deleted successfully');
    return {
      success: true,
      message: 'Conversation deleted successfully'
    };
  } catch (error) {
    console.error('[CHAT-API] Error deleting conversation:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to delete conversation',
      error: error
    };
  }
};

/**
 * Clear all chat history for a client
 * @param clientId - The client ID
 * @returns Promise that resolves to the clear result
 */
export const clearAllChatHistory = async (clientId: string): Promise<any> => {
  try {
    console.log('[CHAT-API] Clearing all chat history for client:', clientId);
    
    await axios.delete(`${API_BASE_URL}/api/chat/history/${clientId}/clear-all`);
    
    console.log('[CHAT-API] All chat history cleared successfully');
    return {
      success: true,
      message: 'All chat history cleared successfully'
    };
  } catch (error) {
    console.error('[CHAT-API] Error clearing chat history:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to clear chat history',
      error: error
    };
  }
};

export const toggleCitationSettings = async (clientId: string, enabled: boolean): Promise<any> => {
  try {
    console.log(`[CITATION-API] Toggling citations for ${clientId} to:`, enabled);
    
    const response = await axios.post(`${API_BASE_URL}/api/citations/toggle/${clientId}`, {
      enable: enabled
    });

    const data = response.data;
    console.log('[CITATION-API] Toggle response:', data);
    
    // Check if the current state matches what we requested
    if (data.success && data.citations_enabled !== enabled) {
      console.warn('[CITATION-API] Warning: Server returned different state than requested', {
        requested: enabled,
        returned: data.citations_enabled
      });
    }
    
    return data;
  } catch (error) {
    console.error('[CITATION-API] Error toggling citation settings:', error);
    throw error;
  }
};

export const updateAllowedDomains = async (clientId: string, allowedDomains: string[]) => {
    const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/allowed-domains`, {
        allowed_domains: allowedDomains
    });
    return response.data;
};

// Client Management APIs
// The Command Deck aggregate — revenue, MRR, clients, sessions, loop, team,
// per-client margin. Super-only, 60s cached server-side.
export const getCommandDeck = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/super/command-deck`);
    return response.data;
};

// Machine console surfaces (CD3 read proxy): experts | capabilities | platform | audit | projects | lanes | controls.
export const getDeckMachineSurface = async (surface: string, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : '';
    const response = await axios.get(`${API_BASE_URL}/api/super/machine/${surface}${qs}`);
    return response.data;
};

// CD3 writes — audited on the builder with the acting super's email.
export const sendDeckMachineWrite = async (surface: string, body: any, method: 'post' | 'patch' = 'post') => {
    const response = await axios[method](`${API_BASE_URL}/api/super/machine/${surface}`, body);
    return response.data;
};

export const deckMachineRequest = async (method: 'get' | 'post' | 'put' | 'patch' | 'delete', surface: string, body?: any, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : '';
    const response = await axios.request({ method, url: `${API_BASE_URL}/api/super/machine/${surface}${qs}`, data: body });
    return response.data;
};

// Machine deck DELETE passthrough (e.g. disconnect a notification routing).
export const deleteDeckMachine = async (surface: string, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : '';
    const response = await axios.delete(`${API_BASE_URL}/api/super/machine/${surface}${qs}`);
    return response.data;
};

// Capabilities GitHub import (inspect/import) — a distinct sub-path from the
// generic machine/{surface} write above, since it targets a different
// builder route (.../capabilities/import) with a different body shape.
export const sendDeckCapabilitiesImport = async (body: { action: 'inspect' | 'import'; source: string; keys?: string[] }) => {
    const response = await axios.post(`${API_BASE_URL}/api/super/machine/capabilities/import`, body);
    return response.data;
};

// Capabilities zip upload (inspect/import) — multipart; the file is never
// buffered into JSON, it rides straight through to the builder.
export const sendDeckCapabilitiesZipImport = async (action: 'inspect' | 'import', file: File, keys?: string[]) => {
    const form = new FormData();
    form.append('action', action);
    form.append('keys', JSON.stringify(keys || []));
    form.append('file', file);
    const response = await axios.post(`${API_BASE_URL}/api/super/machine/capabilities/import-zip`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

// Per-project super controls (2026-10-11): pause/resume/stop builds + clean delete.
export const sendProjectControl = async (projectId: string, action: 'pause' | 'resume' | 'stop_builds') => {
    const response = await axios.post(`${API_BASE_URL}/api/super/machine/projects/${projectId}/controls`, { action });
    return response.data;
};
export const getProjectDeleteManifest = async (projectId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/super/machine/projects/${projectId}/delete-manifest`);
    return response.data;
};
export const deleteBuilderProject = async (projectId: string, confirmToken: string) => {
    const response = await axios.delete(`${API_BASE_URL}/api/super/machine/projects/${projectId}?confirm=${encodeURIComponent(confirmToken)}`);
    return response.data;
};

// Provider console — Agents Anywhere's own company view (engagements, engineers,
// departments/roster/loop). Provider owner/lead or super only.
export const getProviderOverview = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/provider/overview`);
    return response.data;
};

// One cached aggregate for the client dashboard — real spend, real quota
// (no hard-coded zeros), pipeline, and the work-plane delivery slice.
export const getClientDashboardOverview = async (clientId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/dashboard/overview/${clientId}`);
    return response.data;
};

// Add a teammate (expert / team-lead) to the provider company's workspace.
export const addProviderMember = async (
    providerId: string,
    body: { email: string; name?: string; role: string; department_id?: string | null }
) => {
    const response = await axios.post(
        `${API_BASE_URL}/api/workspace/${providerId}/members`, body
    );
    return response.data;
};

// Invite a person into a company's workspace, or add them to it without an
// email. The same endpoint the team screen uses; the Room reaches it when a
// department needs a head who is not on the roster yet.
export const inviteCompanyMember = async (
    clientId: string,
    body: { email: string; name?: string; role: string; department_id?: string | null; send_email?: boolean }
) => {
    const response = await axios.post(
        `${API_BASE_URL}/api/workspace/${clientId}/members`, body
    );
    return response.data;
};

// Change a member's workspace role, or move them to a department. The team
// screen and the Room's team onboarding both write through here.
export const patchCompanyMember = async (
    clientId: string,
    email: string,
    body: { role?: string; department_id?: string | null }
) => {
    const response = await axios.patch(
        `${API_BASE_URL}/api/workspace/${clientId}/members/${encodeURIComponent(email)}`, body
    );
    return response.data;
};

// A team-lead assigns an open request to an engineer (within their department).
export const assignProviderRequest = async (requestId: string, assigneeEmail: string) => {
    const response = await axios.post(
        `${API_BASE_URL}/api/workspace/expert/requests/${requestId}/assign`,
        { assignee_email: assigneeEmail }
    );
    return response.data;
};

// The provider's assignable people (Experts + Team Leads) — options for the
// per-project assign control.
export const getProviderExperts = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/provider/experts`);
    return response.data;
};

// Assign an engineer/expert directly to a project (builder Project.expertId).
// The live-backfill hand — super (incl. impersonating) or a provider member.
export const assignExpertToProject = async (projectId: string, email: string, serviceModel: 'BLOCKER_SUPPORT' | 'FDE' = 'BLOCKER_SUPPORT') => {
    const response = await axios.post(
        `${API_BASE_URL}/api/provider/projects/${projectId}/assign-expert`, { email, service_model: serviceModel }
    );
    return response.data;
};

// Role-aware project roster for the /projects page.
//   viewer 'owner'     → { ours[], client_work[], totals }
//   viewer 'developer' → { projects[] }  (only the ones assigned to them)
// Rows carry expert, pipeline phase (shipped), ticket mix, blockers, stuck
// verdict and dates — the rich builder admin/projects channel, role-scoped.
export const getProjectsRoster = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/provider/projects/roster`);
    return response.data;
};

// Platform kill switch — revoke every live session. Super only. Signs
// everyone (including the caller) out; forces a fresh login everywhere.
export const revokeAllSessions = async () => {
    const response = await axios.post(`${API_BASE_URL}/api/auth/sessions/revoke-all`);
    return response.data;
};

// The internal (AA) company: departments with leads + team size. null when unset.
export const getInternalCompany = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/super/internal-company`);
    return response.data;
};

// Mark/unmark a company as THE internal company (super only; exactly one).
export const setCompanyInternal = async (clientId: string, internal: boolean) => {
    const response = await axios.patch(`${API_BASE_URL}/api/super/clients/${clientId}/internal`, { internal });
    return response.data;
};

// Every provider company besides the main one (super only).
export const listProviders = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/super/providers`);
    return response.data;
};

// Make a company a provider, or stop it being one (super only).
export const setCompanyProvider = async (clientId: string, provider: boolean, ownerBuilderRoles = false) => {
    const response = await axios.patch(`${API_BASE_URL}/api/super/clients/${clientId}/provider`, { provider, owner_builder_roles: ownerBuilderRoles });
    return response.data;
};

// A provider company's Owner adds a client; its owner is invited unless send_invite is false.
export const createProviderClient = async (body: { name: string; email: string; contact_name?: string; phone?: string; website?: string; send_invite?: boolean }) => {
    const response = await axios.post(`${API_BASE_URL}/api/provider/clients`, body);
    return response.data;
};

export const sendProviderClientInvite = async (clientId: string, email?: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/provider/clients/${clientId}/invite`, { email: email || null });
    return response.data;
};

// A provider's client link: whoever sets up a company through it lands under this provider.
export const getProviderClientLink = async () => {
    const response = await axios.post(`${API_BASE_URL}/api/provider/client-link`, {});
    return response.data as { url: string; expires_at: string; expires_days: number };
};

export const getProviderClientInviteLink = async (clientId: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/provider/clients/${clientId}/invite-link`, {});
    return response.data;
};

export const getAllClients = async () => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/consultant/${consultantId}/clients`);
        return response.data;
    } catch (error) {
        console.error('Error fetching clients:', error);
        throw new Error('Failed to fetch clients');
    }
};

export const getClientDetails = async (clientId: string) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;

        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }

        const response = await axios.get(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}/details`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch client details');
        }
        throw new Error('Failed to fetch client details');
    }
};

// ── Marketplace matches (consultant surface) ────────────────────────────
// Same matcher the Orbit chat card uses; here keyed to a specific client so
// the consultant's client detail page can surface the product path.
export interface MarketplaceMatch {
    solution: {
        id?: string;
        slug: string;
        name: string;
        tagline?: string;
        description?: string;
        landing_url?: string;
        [key: string]: any;
    };
    score: number;
    reasons: string[];
}

export const getMarketplaceMatches = async (clientId: string) => {
    const response = await axios.get(`${getApiBaseUrl()}/marketplace/match`, {
        params: { client_id: clientId },
    });
    return response.data as { matches: MarketplaceMatch[]; count: number };
};

export interface CreateClientPayload {
    name: string;            // business / client account name (load-bearing)
    email: string;           // dummy email allowed — no OAuth required
    phone?: string;          // call target for Layer 3 outbound voice
    website?: string;
    contact_name?: string;   // primary contact; greeted by name on the call
    password?: string;       // omit — the backend generates a secure one server-side
    send_email?: boolean;    // default true — welcome email with sign-in credentials
    context?: Record<string, unknown>;  // optional business dossier prefill (industry, ...)
    departments?: string[]; // department names created on the new client
    read_site?: boolean;    // default true — false when the caller narrates the read itself
}

export const createClient = async (payload: CreateClientPayload) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
        throw new Error('Authentication token not found');
    }
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const consultantId = tokenPayload.consultant_id;
    if (!consultantId) {
        throw new Error('Consultant ID not found in token');
    }
    // No client-side password generation: when omitted, the backend creates
    // a secure random password server-side (never logged or returned). The
    // consultant shares an Orbit link (Layer 2), not credentials.
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${consultantId}/clients`,
            payload
        );
        // `prefill` reports which dossier fields the server actually applied —
        // a key the client's registry does not carry is skipped, so the caller
        // must report the server's answer rather than what was typed.
        return response.data as {
            success: boolean;
            message: string;
            client_id: string;
            website?: string;
            prefill?: { applied: string[]; skipped: string[] };
        };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to create client');
        }
        throw new Error('Failed to create client');
    }
};

// Mint a pre-authenticated Orbit intake link for a client (consultant action).
export const generateIntakeLink = async (clientId: string, expiresDays = 14) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication token not found');
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const consultantId = tokenPayload.consultant_id;
    if (!consultantId) throw new Error('Consultant ID not found in token');
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}/intake-link`,
            { expires_days: expiresDays }
        );
        return response.data as { token_id: string; url: string; expires_at: string };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to generate intake link');
        }
        throw new Error('Failed to generate intake link');
    }
};

const consultantIdFromToken = (): string => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication token not found');
    const consultantId = JSON.parse(atob(token.split('.')[1])).consultant_id;
    if (!consultantId) throw new Error('Consultant ID not found in token');
    return consultantId;
};

export interface DomainConflict {
    id: string;
    name: string;
    domain: string;
}

// The duplicate-client guard, run while the partner is still on the Company
// step. Same domain-only rule the create route enforces.
export const checkClientDomain = async (email: string) => {
    const consultantId = consultantIdFromToken();
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/consultant/${consultantId}/clients/check-domain`,
            { params: { email } }
        );
        return response.data as { conflict: DomainConflict | null };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Could not check this email domain');
        }
        throw new Error('Could not check this email domain');
    }
};

export interface RosterPreview {
    people: { name: string; role: string; note: string }[];
    departments: string[];
    skipped: number;
}

// Read a roster CSV into people and department names. Nobody is invited.
export const previewRoster = async (csv: string) => {
    const consultantId = consultantIdFromToken();
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${consultantId}/roster-preview`,
            { csv }
        );
        return response.data as RosterPreview;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Could not read that CSV');
        }
        throw new Error('Could not read that CSV');
    }
};

// Mint a partner referral link. It is not bound to a client: whoever sets up
// a company through it is added to this Solution Partner's clients.
export const createReferralLink = async (expiresDays = 30) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication token not found');
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const consultantId = tokenPayload.consultant_id;
    if (!consultantId) throw new Error('Consultant ID not found in token');
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${consultantId}/referral-link`,
            { expires_days: expiresDays }
        );
        return response.data as { url: string; expires_at: string; expires_days: number };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to prepare the client link');
        }
        throw new Error('Failed to prepare the client link');
    }
};

// Mint an Orbit intake link and email it to the client owner. This is the
// invite that follows Add a client: the owner lands in their own onboarding with
// the partner's context already known, and no credentials travel by email.
export const sendIntakeInvite = async (clientId: string, expiresDays = 14, email?: string) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication token not found');
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const consultantId = tokenPayload.consultant_id;
    if (!consultantId) throw new Error('Consultant ID not found in token');
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}/intake-link/send`,
            { expires_days: expiresDays, ...(email ? { email } : {}) }
        );
        return response.data as { sent: boolean; email: string; token_id: string; url: string; expires_at: string };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to send the invite');
        }
        throw new Error('Failed to send the invite');
    }
};

// Exchange an intake token for a client session (public — no auth header needed).
export const redeemIntakeLink = async (tokenId: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/auth/intake-link/${tokenId}`);
    return response.data as {
        token: string;
        refresh_token?: string;
        expires_in?: number;
        user: { id: string; name: string; email: string; role: string; client_id: string; consultant_id: string | null };
    };
};

// Schedule (or immediately place) an outbound Orbit discovery call.
// scheduledAt omitted/empty → call now. Empty toNumber → backend uses client.phone.
export const scheduleVoiceCall = async (
    clientId: string,
    opts: { toNumber?: string; scheduledAt?: string; problemId?: string } = {}
) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/intake/voice/schedule`, {
            client_id: clientId,
            to_number: opts.toNumber || undefined,
            scheduled_at: opts.scheduledAt || undefined,
            problem_id: opts.problemId || undefined,
        });
        return response.data as { call_id: string; status: string; scheduled_at: string | null };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to schedule call');
        }
        throw new Error('Failed to schedule call');
    }
};

export const listVoiceCalls = async (clientId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/intake/voice/calls`, {
        params: { client_id: clientId },
    });
    return (response.data?.calls || []) as Array<{
        id: string; status: string; scheduled_at: string | null;
        started_at: string | null; ended_at: string | null;
        summary: string | null; error: string | null; created_at: string | null;
    }>;
};

export const updateClient = async (clientId: string, data: any) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.put(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}`, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to update client');
        }
        throw new Error('Failed to update client');
    }
};

/** Admin action: email a password-reset link to the client's login address. */
export const sendClientPasswordReset = async (clientId: string) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}/send-reset`
        );
        return response.data as { success: boolean; email: string; message: string };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to send password reset');
        }
        throw new Error('Failed to send password reset');
    }
};

export const deleteClient = async (clientId: string) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.delete(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to delete client');
        }
        throw new Error('Failed to delete client');
    }
};

// Role Management APIs
export const getRoles = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/roles`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch roles');
    }
};

export const createRole = async (data: { name: string; description: string; permissions: string[] }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/roles`, data);
        return response.data;
    } catch (error) {
        throw new Error('Failed to create role');
    }
};

export const getPermissions = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/permissions`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch permissions');
    }
};

export const updateRole = async (roleId: string, data: any) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/api/roles/${roleId}`, data);
        return response.data;
    } catch (error) {
        throw new Error('Failed to update role');
    }
};

export const deleteRole = async (roleId: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/api/roles/${roleId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to delete role');
    }
};

// Consultant Client APIs
export const getClientStats = async (clientId: string) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}/stats`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch client statistics');
    }
};

export const getClientChatHistory = async (clientId: string, page: number, limit: number = 10) => {
    try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
            throw new Error('User ID not found');
        }
        const response = await axios.get(
            `${API_BASE_URL}/api/consultant/${userId}/clients/${clientId}/chat-history?page=${page}&limit=${limit}`
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch chat history');
    }
};

/* The path segment is the CONSULTANT id, not the caller's user id. It sent the
   user id for as long as this existed, which a super survives because their
   branch of the route skips the ownership check entirely; a Solution Partner
   hit "Not authorized to impersonate this client" every time. The id lives in
   the token, which is where the rest of this file reads it from. */
export const impersonateClient = async (clientId: string) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Not signed in');
    let consultantId = '';
    try {
        consultantId = JSON.parse(atob(token.split('.')[1])).consultant_id || '';
    } catch {
        consultantId = '';
    }
    const actor = consultantId || localStorage.getItem('user_id');
    if (!actor) throw new Error('No consultant on this session');
    const response = await axios.post(
        `${API_BASE_URL}/api/consultant/${actor}/impersonate/${clientId}`,
        {},
    );
    return response.data;
};

/* Work as a Solution Partner: the super becomes that partner's own login for
   an hour. The server picks which login and says which it picked, so nothing
   here needs to know how a partner is wired to a user row. Its error detail is
   worth showing: "no login to work as" is a real state, not a failure. */
export const impersonatePartner = async (consultantId: string) => {
    const response = await axios.post(
        `${API_BASE_URL}/api/super/partners/${consultantId}/impersonate`,
        {},
    );
    return response.data;
};

// Consultant management functions
export const getAllConsultants = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/consultant/consultants`);
        return response.data;
    } catch (error) {
        console.error('Error fetching consultants:', error);
        throw error;
    }
};

export const getConsultantDetails = async (consultantId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/consultant/consultants/${consultantId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching consultant details for ${consultantId}:`, error);
        throw error;
    }
};

export const createConsultant = async (consultantData: {
    name: string;
    email: string;
    /** Left out, the partner is invited and chooses their own on the accept link. */
    password?: string;
}): Promise<{ success: boolean; consultant_id: string; invite_url: string | null; consultant: { id: string; name: string; email: string } }> => {
    try {
        console.log('Creating consultant with data:', consultantData);
        
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/consultants`,
            consultantData
        );

        console.log('Consultant creation response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('Error creating consultant:', error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            throw new Error('Session expired. Please login again.');
        }
        
        // Email already exists - direct from FastAPI
        if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
            // Extract error message without status code prefix
            const errorDetail = error.response.data.detail;
            
            // Check if it contains the HTTP status code prefix (e.g., "400: ")
            if (errorDetail.match(/^\d{3}:\s/)) {
                // Remove the status code prefix (like "400: ")
                const cleanMessage = errorDetail.replace(/^\d{3}:\s/, '');
                throw new Error(cleanMessage);
            }
            
            // Check for email already exists message
            if (errorDetail.includes('Email already exists')) {
                throw new Error('This email address is already registered. Please use a different email address.');
            }
            
            // Otherwise return the original detail
            throw new Error(errorDetail);
        }
        
        // PostgreSQL duplicate key error from database
        if (error.response?.data?.detail?.includes('duplicate key value violates unique constraint')) {
            throw new Error('This email address is already registered. Please use a different email.');
        }
        
        // Fallback to a generic error message
        throw new Error(error.message || 'Failed to create consultant. Please try again.');
    }
};

export const updateConsultant = async (consultantId: string, consultantData: {
    name: string;
    email: string;
    password?: string;
}) => {
    try {
        console.log('Updating consultant with data:', consultantData);
        
        const response = await axios.put(
            `${API_BASE_URL}/api/consultant/consultants/${consultantId}`,
            consultantData
        );

        console.log('Consultant update response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error(`Error updating consultant ${consultantId}:`, error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            throw new Error('Session expired. Please login again.');
        }
        
        if (error.response?.status === 403) {
            throw new Error('Not authorized to update this consultant');
        }
        
        if (error.response?.status === 404) {
            throw new Error('Consultant not found');
        }
        
        // Email already exists error
        if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
            const errorDetail = error.response.data.detail;
            
            if (errorDetail.includes('Email already exists')) {
                throw new Error('This email address is already registered. Please use a different email address.');
            }
            
            throw new Error(errorDetail);
        }
        
        // Fallback to a generic error message
        throw new Error(error.message || 'Failed to update consultant. Please try again.');
    }
};

export const deleteConsultant = async (consultantId: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/api/consultant/consultants/${consultantId}`);
        return response.data;
    } catch (error: any) {
        console.error(`Error deleting consultant ${consultantId}:`, error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            throw new Error('Session expired. Please login again.');
        }
        
        if (error.response?.status === 404) {
            throw new Error('Consultant not found');
        }
        
        if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
            throw new Error(error.response.data.detail);
        }
        
        throw new Error(error.message || 'Failed to delete consultant');
    }
};

/* Solution Partner invites: the admin sends a link, the partner sets their own
   password there and lands on the welcome deck. */
export const sendConsultantInvite = async (consultantId: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/consultant/consultants/${consultantId}/invite`);
        return response.data as { url: string; expires_at: string; email: string };
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Could not send the invite');
    }
};

export const getConsultantInviteLink = async (consultantId: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/consultant/consultants/${consultantId}/invite-link`);
        return response.data as { url: string; expires_at: string };
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Could not prepare the invite link');
    }
};

export const getPartnerInvite = async (token: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/partner-invite/${token}`);
    return response.data as { name: string; email: string; inviter_name: string; inviter_email: string; expires_at: string };
};

export const acceptPartnerInvite = async (token: string, body: { name: string; password: string; phone?: string }) => {
    const response = await axios.post(`${API_BASE_URL}/api/partner-invite/${token}/accept`, body);
    return response.data as {
        token: string; refresh_token?: string; expires_in?: number;
        user: { id: string; name: string; email: string; role: string; consultant_id: string | null; client_id: string | null; needs_onboarding: boolean };
    };
};

export const getPartnerOnboarding = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/consultant/consultants/me/onboarding`);
    return response.data as { onboarded: boolean; name: string; email: string };
};

export const finishPartnerOnboarding = async () => {
    const response = await axios.post(`${API_BASE_URL}/api/consultant/consultants/me/onboarding/done`);
    return response.data as { onboarded: boolean };
};

export const provisionConsultantAgentBuilder = async (consultantId: string) => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/consultants/${consultantId}/provision-agent-builder`
        );
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to provision Agent Builder');
    }
};

export const getAgentBuilderStatus = async () => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/consultant/consultants/agent-builder-status`
        );
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to check Agent Builder status');
    }
};

export const selfProvisionAgentBuilder = async () => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/consultants/self-provision-agent-builder`
        );
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to provision Agent Builder');
    }
};

/**
 * Check builder integration status for the current client workspace.
 * Returns { connected: boolean, validxAppUrl: string | null }.
 */
export const getBuilderStatus = async () => {
    const response = await axios.get(
        `${API_BASE_URL}/api/workspace/builder-status`
    );
    return response.data;
};

/**
 * Request a signed bridge URL to open the builder for the current client workspace.
 * Lazy-provisions the workspace on builder if it doesn't exist yet.
 * Always signs for the client's founder email (not the caller's).
 * Returns { url: string, workspace_id: string, redirect: string }.
 */
/**
 * The problem's dedicated workspace scoping agent (created once on the
 * builder, reused forever). Returns { agent_path, conversation_id,
 * project_id, client_id } — feed agent_path to requestAgentBridgeUrl and
 * open the result in a new tab.
 */
export const requestProblemWorkspaceScope = async (problemId: string) => {
    const response = await axios.post(`${API_BASE_URL}/api/scope/problems/${problemId}/workspace-scope`);
    return response.data;
};

export const requestAgentBridgeUrl = async (redirect?: string, workspaceId?: string, projectId?: string) => {
    const response = await axios.post(
        `${API_BASE_URL}/api/workspace/request-agent`,
        { redirect: redirect || null, workspace_id: workspaceId || null, project_id: projectId || null },
    );
    return response.data;
};

// Add new function for paginated recent citations
export const getRecentCitationsPaginated = async (clientId: string, page: number, limit: number) => {
  try {
    // Use axios consistent with getCitationAnalytics
    const response = await axios.get(`${API_BASE_URL}/api/citations/recent/${clientId}?page=${page}&limit=${limit}`);
    return response.data; // axios returns data directly
  } catch (error) {
    console.error('Error fetching paginated recent citations:', error);
    // Re-throw or return a structured error based on existing patterns
    throw new Error('Failed to fetch recent citations'); 
  }
};

export const uploadDocumentsWithProgress = async (
    formData: FormData, 
    onProgress?: (data: any) => void,
    onError?: (error: any) => void
): Promise<any> => {
    try {
        // Make the initial upload request
        const response = await axios.post(`${API_BASE_URL}/api/documents/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        const responseData = response.data;
        
        // If progress tracking is requested and we got an upload_id, setup a stream
        if (onProgress && responseData.upload_id) {
            // Create stream connection to track upload progress
            const eventSource = createDocumentUploadStream(responseData.upload_id, onProgress, onError);
            
            // Attach the close method to the response
            responseData.closeStream = () => eventSource.close();
        }

        return responseData;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to upload documents');
        }
        throw error;
    }
};

/**
 * Creates a server-sent event connection to monitor document upload status.
 * 
 * @param uploadId Upload ID to monitor
 * @param onMessage Optional callback for handling messages
 * @param onError Optional callback for errors
 * @returns EventSource object
 */
export const createDocumentUploadStream = (
    uploadId: string,
    onMessage?: (data: any) => void,
    onError?: (error: any) => void
): EventSource => {
    const streamUrl = `${API_BASE_URL}/api/documents/upload/${uploadId}/stream`;
    
    console.log(`[UPLOAD-API] Creating stream to: ${streamUrl}`);
    
    // Create EventSource for streaming updates
    const eventSource = new EventSource(streamUrl);
    
    // Flag to track if we've detected any error message
    let errorDetected = false;
    
    // If callbacks are provided, connect them
    if (onMessage) {
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Check for error message
                if (data.error) {
                    console.log(`[UPLOAD-API] Error detected: ${data.error}`);
                    errorDetected = true;
                    eventSource.close(); // Close the connection
                    
                    // Still call onMessage so client can handle this status
                    onMessage(data);
                    
                    // Call error handler if provided
                    if (onError) onError(data.error);
                    return;
                }
                
                // Process normal messages
                onMessage(data);
                
                // Check if upload is complete or failed
                if (data.status === 'completed' || data.status === 'failed') {
                    console.log(`[UPLOAD-API] Upload ${data.status}, closing stream`);
                    eventSource.close();
                }
            } catch (e) {
                console.error(`[UPLOAD-API] Error parsing event data:`, e);
                if (onError) onError(e);
            }
        };
    }
    
    // Custom error handler
    eventSource.onerror = (error) => {
        console.error(`[UPLOAD-API] Stream error:`, error);
        
        // Call the provided error callback
        if (onError) onError(error);
        
        // Close connection if we already detected an error to prevent reconnection
        if (errorDetected) {
            eventSource.close();
        }
    };
    
    return eventSource;
};

export const getClientDocuments = async (
    clientId: string,
    page: number = 1,
    limit: number = 50,
    search?: string
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        if (search) params.append('search', search);
        const response = await axios.get(`${API_BASE_URL}/api/documents/client/${clientId}?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client documents:', error);
        throw new Error('Failed to fetch documents');
    }
};

// Add function to fetch document content by ID using existing endpoint
export const getDocumentContent = async (documentId: string) => {
    try {
        console.log(`Fetching content for document ID: ${documentId}`);
        const response = await axios.get(`${API_BASE_URL}/api/documents/${documentId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching document content:', error);
        throw new Error('Failed to fetch document content');
    }
};

// Delete a document
export const deleteDocument = async (documentId: string) => {
    try {
        console.log(`Deleting document ID: ${documentId}`);
        const response = await axios.delete(`${API_BASE_URL}/api/documents/${documentId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting document:', error);
        throw new Error('Failed to delete document');
    }
};

/**
 * =====================
 * THEME ENDPOINTS
 * =====================
 */

/**
 * Get active/default theme + layout for a client
 */
export const getClientTheme = async (clientId: string): Promise<any> => {
    if (!clientId) throw new Error('clientId is required');
    try {
        const response = await axios.get(`${getApiBaseUrl()}/themes/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching client theme', error);
        throw error;
    }
};

/**
 * Save / upsert default theme for a client
 */
export const saveClientTheme = async (clientId: string, payload: any): Promise<any> => {
    if (!clientId) throw new Error('clientId is required');
    try {
        const response = await axios.post(`${getApiBaseUrl()}/themes/${clientId}`, payload);
        return response.data;
    } catch (error) {
        console.error('[API] Error saving client theme', error);
        throw error;
    }
};

// QA Validation API Functions

/**
 * Generate Q&A pairs for client validation
 */
export const generateQAValidation = async (
    clientId: string,
    numQuestions: number = 10,
    opts?: {
        source_filters?: {
            page_urls?: string[];
            document_ids?: (string|number)[];
            youtube_ids?: (string|number)[];
        };
        generation_mode?: 'combined'|'per_source';
        custom_questions?: string[];
        options?: Record<string, any>;
    }
): Promise<any> => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/qa-validation/generate`, {
            client_id: clientId,
            num_questions: numQuestions,
            ...(opts?.source_filters ? { source_filters: {
                page_urls: opts.source_filters.page_urls || [],
                document_ids: (opts.source_filters.document_ids || []).map(String),
                youtube_ids: (opts.source_filters.youtube_ids || []).map(String)
            }} : {}),
            ...(opts?.generation_mode ? { generation_mode: opts.generation_mode } : {}),
            ...(opts?.custom_questions ? { custom_questions: opts.custom_questions } : {}),
            ...(opts?.options ? { options: opts.options } : {})
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error generating QA validation', error);
        throw error;
    }
};

/**
 * Generate Q&A pairs with SSE streaming for progress updates
 */
export const generateQAValidationStream = (
    clientId: string,
    numQuestions: number = 10,
    opts?: {
        source_filters?: {
            page_urls?: string[];
            document_ids?: (string | number)[];
            youtube_ids?: (string | number)[];
        };
        generation_mode?: 'combined' | 'per_source';
        custom_questions?: string[];
        options?: Record<string, any>;
    },
    callbacks?: {
        onProgress?: (data: any) => void;
        onQAPair?: (data: any) => void;
        onComplete?: (data: any) => void;
        onError?: (error: string) => void;
    }
): { abort: () => void } => {
    const controller = new AbortController();
    const { signal } = controller;

    const authToken = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
    };

    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }

    const postData = {
        client_id: clientId,
        num_questions: numQuestions,
        ...(opts?.source_filters ? {
            source_filters: {
                page_urls: opts.source_filters.page_urls || [],
                document_ids: (opts.source_filters.document_ids || []).map(String),
                youtube_ids: (opts.source_filters.youtube_ids || []).map(String)
            }
        } : {}),
        ...(opts?.generation_mode ? { generation_mode: opts.generation_mode } : {}),
        ...(opts?.custom_questions ? { custom_questions: opts.custom_questions } : {}),
        ...(opts?.options ? { options: opts.options } : {})
    };

    const apiUrl = `${getApiBaseUrl()}/qa-validation/generate/stream`;

    fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(postData),
        signal,
        credentials: 'omit',
        mode: 'cors'
    }).then(response => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Could not get response reader');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    let currentEventType = '';
                    for (const line of lines) {
                        if (line.startsWith('event:')) {
                            currentEventType = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            try {
                                const data = JSON.parse(line.slice(5).trim());

                                if (currentEventType === 'error' || data.event_type === 'error') {
                                    callbacks?.onError?.(data.error || 'Unknown error');
                                } else if (currentEventType === 'complete' || data.event_type === 'complete') {
                                    callbacks?.onComplete?.(data);
                                } else if (data.event_type === 'qa_pair' || data.event_type === 'question') {
                                    callbacks?.onQAPair?.(data);
                                } else {
                                    callbacks?.onProgress?.(data);
                                }
                            } catch (parseError) {
                                console.warn('[QA-API] Failed to parse SSE data:', line);
                            }
                        }
                    }
                }
            } catch (error: any) {
                if (error.name !== 'AbortError') {
                    console.error('[QA-API] Stream processing error:', error);
                    callbacks?.onError?.(error.message || 'Stream processing failed');
                }
            }
        };

        processStream();
    }).catch(error => {
        if (error.name !== 'AbortError') {
            console.error('[QA-API] Fetch error:', error);
            callbacks?.onError?.(error.message || 'Failed to start QA generation');
        }
    });

    return { abort: () => controller.abort() };
};

/**
 * Get QA validation sessions for a client
 */
export const getQAValidationSessions = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/qa-validation/sessions/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching QA validation sessions', error);
        throw error;
    }
};

/**
 * Update QA validation session status
 */
export const updateQASessionStatus = async (sessionId: string, status: string, reviewedBy: string): Promise<any> => {
    try {
        const response = await axios.put(`${getApiBaseUrl()}/qa-validation/session/status`, {
            session_id: sessionId,
            status: status,
            reviewed_by: reviewedBy
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error updating QA session status', error);
        throw error;
    }
};

/**
 * Get client QA validation status
 */
export const getClientQAStatus = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/qa-validation/client/${clientId}/status`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching client QA status', error);
        throw error;
    }
};

/**
 * Delete QA validation session
 */
export const deleteQASession = async (sessionId: string): Promise<any> => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/qa-validation/session/${sessionId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting QA session', error);
        throw error;
    }
};

/**
 * Submit QA validation feedback
 */
export const submitQAFeedback = async (sessionId: string, qaPairIndex: number, feedbackType: string, reason?: string, additionalComments?: string): Promise<any> => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/qa-validation/feedback`, {
            session_id: sessionId,
            qa_pair_index: qaPairIndex,
            feedback_type: feedbackType,
            reason: reason,
            additional_comments: additionalComments || ''
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error submitting QA feedback', error);
        throw error;
    }
};

/**
 * Get chat messages with feedback status
 */
export const getMessagesWithFeedback = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/api/feedback/${clientId}/messages`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching messages with feedback', error);
        throw error;
    }
};

/**
 * Get list of users for a client with conversation counts
 */
export const getClientUsers = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/users`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching client users', error);
        throw error;
    }
};

/**
 * Get conversations for a specific user
 */
export const getUserConversations = async (clientId: string, userId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/users/${userId}/conversations`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching user conversations', error);
        throw error;
    }
};

/**
 * Get all conversations for a client with pagination
 */
export const getAllConversations = async (clientId: string, offset: number = 0, limit: number = 50): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/conversations`, {
            params: { offset, limit }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching all conversations', error);
        throw error;
    }
};

/**
 * Get all messages in a specific conversation
 */
export const rejectConversation = async (clientId: string, conversationId: string, reason: string): Promise<any> => {
    try {
        const reviewerId = localStorage.getItem('user_id') || 'system';
        
        const response = await axios.post(
            `${getApiBaseUrl()}/feedback/${clientId}/conversations/${conversationId}/reject`,
            {
                rejected_by: reviewerId,
                rejection_reason: reason
            }
        );

        return response.data;
    } catch (error: any) {
        console.error('Error rejecting conversation:', error);
        throw error;
    }
};

export const getConversationRejectionStatus = async (clientId: string, conversationId: string): Promise<any> => {
    try {
        const response = await axios.get(
            `${getApiBaseUrl()}/feedback/${clientId}/conversations/${conversationId}/rejection-status`
        );

        return response.data;
    } catch (error: any) {
        console.error('Error getting conversation rejection status:', error);
        throw error;
    }
};

export const getConversationMessages = async (clientId: string, userId: string, conversationId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/users/${userId}/conversations/${conversationId}/messages`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching conversation messages', error);
        throw error;
    }
};

export const deleteConversationMessage = async (
    clientId: string,
    userId: string,
    conversationId: string,
    messageId: string
): Promise<any> => {
    try {
        const response = await axios.delete(
            `${getApiBaseUrl()}/feedback/${clientId}/users/${userId}/conversations/${conversationId}/messages/${messageId}`
        );
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting conversation message', error);
        throw error;
    }
};

// SEO Analysis Functions
export const runSEOAnalysis = async (clientId: string, analysisType: string = 'full') => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/seo/analyze`, {
            client_id: clientId,
            analysis_type: analysisType
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error running SEO analysis', error);
        throw error;
    }
};

export const startSEOAnalysisSession = async (clientId: string, analysisType: string = 'full', sessionName?: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/seo/start-session`, {
            client_id: clientId,
            analysis_type: analysisType,
            session_name: sessionName
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error starting SEO analysis session', error);
        throw error;
    }
};

export const getSEOAnalysisSessionStatus = async (sessionId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/session/${sessionId}/status`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting SEO analysis session status', error);
        throw error;
    }
};

export const streamSEOAnalysisStatus = (sessionId: string, onMessage: (data: any) => void, onError?: (error: any) => void) => {
    const eventSource = new EventSource(`${getApiBaseUrl()}/seo/session/${sessionId}/stream`);
    
    eventSource.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data);
            onMessage(data);
        } catch (error) {
            console.error('[API] Error parsing SEO stream data:', error);
            if (onError) onError(error);
        }
    };
    
    eventSource.addEventListener('close', () => {
        console.log('[API] SEO analysis stream closed');
        eventSource.close();
    });
    
    eventSource.onerror = (error) => {
        console.error('[API] SEO analysis stream error:', error);
        if (onError) onError(error);
        eventSource.close();
    };
    
    return eventSource;
};

export const getClientSEOSummary = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/client/${clientId}/summary`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client SEO summary', error);
        throw error;
    }
};

export const getClientSEOPages = async (clientId: string, pageUrl?: string) => {
    try {
        const url = pageUrl 
            ? `${getApiBaseUrl()}/seo/client/${clientId}/pages?page_url=${encodeURIComponent(pageUrl)}`
            : `${getApiBaseUrl()}/seo/client/${clientId}/pages`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client SEO pages', error);
        throw error;
    }
};

export const getClientSEORecommendations = async (clientId: string, priority?: string) => {
    try {
        const url = priority 
            ? `${getApiBaseUrl()}/seo/client/${clientId}/recommendations?priority=${priority}`
            : `${getApiBaseUrl()}/seo/client/${clientId}/recommendations`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client SEO recommendations', error);
        throw error;
    }
};

export const getClientAIReadiness = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/client/${clientId}/ai-readiness`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client AI readiness', error);
        throw error;
    }
};

export const getPageSEODetails = async (clientId: string, pageUrl: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/client/${clientId}/pages/${encodeURIComponent(pageUrl)}/details`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting page SEO details', error);
        throw error;
    }
};

export const deleteClientSEOAnalysis = async (clientId: string) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/seo/client/${clientId}/analysis`);
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting client SEO analysis', error);
        throw error;
    }
};

// Real-time SEO APIs
export const getLiveVisitors = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/live-visitors/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting live visitors', error);
        throw error;
    }
};

export const getKeywordRankings = async (clientId: string, limit: number = 50) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/keyword-rankings/${clientId}?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting keyword rankings', error);
        throw error;
    }
};

export const getBacklinkAnalytics = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/backlinks/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting backlink analytics', error);
        throw error;
    }
};

export const getCompetitorAnalysis = async (clientId: string, limit: number = 5) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/competitors/${clientId}?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting competitor analysis', error);
        throw error;
    }
};

export const getPageSpeed = async (clientId: string, pageUrl?: string) => {
    try {
        const url = pageUrl 
            ? `${getApiBaseUrl()}/seo/realtime/page-speed/${clientId}?page_url=${encodeURIComponent(pageUrl)}`
            : `${getApiBaseUrl()}/seo/realtime/page-speed/${clientId}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting page speed', error);
        throw error;
    }
};

export const getSERPFeatures = async (clientId: string, keywords: string[]) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/seo/realtime/serp-features`, {
            keywords,
            client_id: clientId
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error getting SERP features', error);
        throw error;
    }
};

export const getSEODashboardData = async (clientId: string, forceRefresh: boolean = false) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/seo-dashboard/${clientId}`, {
            params: { force_refresh: forceRefresh }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error getting SEO dashboard data', error);
        throw error;
    }
};

// YouTube Video APIs
export const getYouTubeVideos = async (
    clientId: string,
    {
        status,
        page = 1,
        limit = 50,
        search
    }: { status?: string; page?: number; limit?: number; search?: string } = {}
) => {
    try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (status) params.append('status', status);
        if (search) params.append('search', search);
        const url = `${getApiBaseUrl()}/youtube/videos/${clientId}?${params.toString()}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting YouTube videos', error);
        throw error;
    }
};

export const processYouTubeVideo = async (clientId: string, url: string, reviewRequired: boolean = true) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/process/${clientId}`, {
            url,
            review_required: reviewRequired
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error processing YouTube video', error);
        throw error;
    }
};

export const syncYouTubeChannel = async (clientId: string, channelUrl: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/channel/${clientId}`, {
            channel_url: channelUrl
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error syncing YouTube channel', error);
        throw error;
    }
};

export const getYouTubeVideoTranscript = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/transcript`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting video transcript', error);
        throw error;
    }
};

export const fetchYouTubeTranscript = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/fetch-transcript`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching YouTube transcript', error);
        throw error;
    }
};

export const getYouTubeTranscriptionStatus = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/transcription-status`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting YouTube transcription status', error);
        throw error;
    }
};

export const approveYouTubeVideo = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/approve`);
        return response.data;
    } catch (error) {
        console.error('[API] Error approving YouTube video', error);
        throw error;
    }
};

export const deleteYouTubeVideo = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting YouTube video', error);
        throw error;
    }
};

export const retranscribeYouTubeVideo = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/retranscribe`);
        return response.data;
    } catch (error) {
        console.error('[API] Error re-transcribing YouTube video', error);
        throw error;
    }
};

export const getYouTubeProcessingStatus = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/youtube/status/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting YouTube processing status', error);
        throw error;
    }
};

// ==================== Billing & Subscription API ====================

// Get subscription plans
export const getSubscriptionPlans = async () => {
    try {
        const userRole = localStorage.getItem('user_role');
        const includePrivate = userRole === 'super';
        const response = await axios.get(`${getApiBaseUrl()}/billing/plans${includePrivate ? '?include_private=true' : ''}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting subscription plans', error);
        throw error;
    }
};

// Get current subscription
export const getCurrentSubscription = async () => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/subscription`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting current subscription', error);
        throw error;
    }
};

// Get payment methods
export const getPaymentMethods = async () => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/payment-methods`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting payment methods', error);
        throw error;
    }
};

// Get invoices
export const getInvoices = async (limit: number = 10) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/invoices?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting invoices', error);
        throw error;
    }
};

// Plan management (super user only)
export const getAllPlans = async (includePrivate: boolean = false) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/plan-management/plans`, {
            params: { include_private: includePrivate }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error getting plans', error);
        throw error;
    }
};

export const createPlan = async (data: any) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/plan-management/plans`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error creating plan', error);
        throw error;
    }
};

export const updatePlan = async (planId: string, data: any) => {
    try {
        const response = await axios.put(`${getApiBaseUrl()}/plan-management/plans/${planId}`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error updating plan', error);
        throw error;
    }
};

export const deletePlan = async (planId: string, force: boolean = false) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/plan-management/plans/${planId}`, {
            params: { force }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting plan', error);
        throw error;
    }
};

export const duplicatePlan = async (planId: string, newName: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/plan-management/plans/${planId}/duplicate`, null, {
            params: { new_name: newName }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error duplicating plan', error);
        throw error;
    }
};

// Products API
export const getProducts = async (
    clientId: string,
    category?: string,
    page: number = 1,
    limit: number = 50
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        if (category) {
            params.append('category', category);
        }
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}?${params.toString()}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Products API error:', error);
        throw error;
    }
};

export const getProductCategories = async (clientId: string) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}/categories`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Product categories API error:', error);
        throw error;
    }
};

export const getProductCategoryTree = async (clientId: string) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}/category-tree`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Product category tree API error:', error);
        throw error;
    }
};

export const getProductDetail = async (clientId: string, url: string) => {
    try {
        const params = new URLSearchParams({ url });
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}/product?${params.toString()}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Product detail API error:', error);
        throw error;
    }
};

export const cleanKnowledgeBase = async (clientId: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/api/knowledge-base/clean/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error cleaning knowledge base', error);
        throw error;
    }
};

// ── Builder (validx) provisioning status for clients ────────────────────────

export interface BuilderStatus {
    workspaceId: string;
    provisioned: boolean;
    name?: string;
    source?: string;
    status?: string;
    billingEmail?: string | null;
    memberCount?: number;
    projectCount?: number;
}

export interface BuilderStatusBatch {
    statuses: Record<string, BuilderStatus>;
    /** Builder app base URL for this environment (localhost:3000 in dev). */
    builderAppUrl: string | null;
}

export const getBuilderStatusBatch = async (
    clientIds: string[],
): Promise<BuilderStatusBatch> => {
    if (!clientIds.length) return { statuses: {}, builderAppUrl: null };
    const response = await axios.post(
        `${API_BASE_URL}/api/consultant/clients/builder-status-batch`,
        { clientIds },
    );
    return {
        statuses: response.data?.statuses || {},
        builderAppUrl: response.data?.builderAppUrl || null,
    };
};

export const provisionClientOnBuilder = async (clientId: string) => {
    const response = await axios.post(
        `${API_BASE_URL}/api/consultant/clients/${clientId}/provision-builder`,
    );
    return response.data;
};
